import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Box, Typography, CircularProgress, IconButton, styled, keyframes } from '@mui/material';
import { ArrowBack, Menu as MenuIcon } from '@mui/icons-material';
import TradePackagesHeader from '../TradePackages/TradePackagesHeader';
import { ShellTopBarActions } from '../../shell/AppShell';
import FTLoading from '../../shell/FTLoading';
import { useShell } from '../../shell/ShellContext';
import PartnerTradesInline from './PartnerTradesInline';
import TradeBuzzCarousel from './TradeBuzzCarousel';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigation } from '../../contexts/NavigationContext';
import { tradePackagesAPI, managerIntelAPI, tweetsAPI, playersAPI } from '../../utils/api';

const API_URL = process.env.REACT_APP_ENV === 'local' ? 'http://localhost:3000/api' : '/api';
const POSITION_ORDER = { QB: 0, RB: 1, WR: 2, TE: 3, K: 4, DEF: 5, PICK: 6 };
const POSITION_FILTERS = ['ALL', 'QB', 'RB', 'WR', 'TE', 'PICKS'];

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

/* ─── Layout ─────────────────────────────────────────────────────── */

const PageContainer = styled(Box)({
  minHeight: '100vh',
  background: '#FFFFFF',
  color: '#1D1D1F',
  paddingBottom: '120px',
});

const Container = styled(Box)({
  maxWidth: '1080px',
  margin: '0 auto',
  padding: '96px 24px 48px',
  '@media (max-width: 800px)': { padding: '80px 16px 40px' },
});

const StepView = styled(Box)({
  animation: `${fadeIn} 0.35s cubic-bezier(0.16, 1, 0.3, 1)`,
});

/* ─── Stepper ────────────────────────────────────────────────────── */

const StepperWrap = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '40px',
  '@media (max-width: 600px)': { gap: '8px', marginBottom: '32px' },
});

const StepDot = styled(Box)(({ state }) => ({
  width: '24px',
  height: '24px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '12px',
  fontWeight: 600,
  background:
    state === 'done' || state === 'active' ? '#1D1D1F' : '#F5F5F7',
  color:
    state === 'done' || state === 'active' ? '#FFFFFF' : '#86868B',
  flex: '0 0 24px',
}));

const StepLabel = styled(Box)(({ state }) => ({
  fontSize: '13px',
  fontWeight: state === 'active' ? 600 : 500,
  letterSpacing: '-0.01em',
  color: state === 'pending' ? '#86868B' : '#1D1D1F',
}));

const StepLine = styled(Box)({
  flex: 1,
  height: '1px',
  background: '#EBEBED',
});

/* ─── Section primitives ─────────────────────────────────────────── */

const SectionHead = styled(Box)({
  fontSize: '13px',
  fontWeight: 500,
  letterSpacing: '2px',
  textTransform: 'uppercase',
  color: '#86868B',
  marginBottom: '20px',
});

/* ─── Hero / context bar ─────────────────────────────────────────── */

const Hero = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '88px 1fr auto',
  gap: '24px',
  alignItems: 'center',
  marginBottom: '48px',
  '@media (max-width: 800px)': {
    gridTemplateColumns: '1fr',
    marginBottom: '32px',
  },
});

const Avatar = styled(Box)({
  width: '88px',
  height: '88px',
  borderRadius: '50%',
  background: '#F5F5F7',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '28px',
  fontWeight: 600,
  color: '#6E6E73',
});

const HeroName = styled(Box)({
  fontSize: '40px',
  fontWeight: 600,
  letterSpacing: '-0.02em',
  lineHeight: 1.1,
  '@media (max-width: 800px)': { fontSize: '32px' },
});

const HeroSub = styled(Box)({
  fontSize: '16px',
  color: '#6E6E73',
  marginTop: '4px',
});

const TimelinePill = styled(Box)({
  display: 'inline-block',
  fontSize: '12px',
  fontWeight: 600,
  letterSpacing: '1.2px',
  textTransform: 'uppercase',
  background: '#000000',
  color: '#FFFFFF',
  padding: '6px 14px',
  borderRadius: '980px',
  marginTop: '8px',
});

const Pulse = styled(Box)({
  textAlign: 'right',
  fontSize: '14px',
  color: '#6E6E73',
  '@media (max-width: 800px)': { textAlign: 'left' },
  '& b': { color: '#1D1D1F', fontWeight: 600 },
});

const ContextBar = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '14px',
  marginBottom: '32px',
  paddingBottom: '20px',
  borderBottom: '1px solid #EBEBED',
});

const ContextAvatar = styled(Box)({
  width: '40px',
  height: '40px',
  borderRadius: '50%',
  background: '#F5F5F7',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '14px',
  fontWeight: 600,
  color: '#6E6E73',
});

const ContextName = styled(Box)({
  fontSize: '16px',
  fontWeight: 600,
  letterSpacing: '-0.01em',
  color: '#1D1D1F',
});

const ContextSub = styled(Box)({
  fontSize: '13px',
  color: '#86868B',
});

/* ─── Stats ──────────────────────────────────────────────────────── */

const Stats = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  background: '#F5F5F7',
  borderRadius: '18px',
  padding: '24px 32px',
  marginBottom: '48px',
  '@media (max-width: 600px)': {
    gridTemplateColumns: 'repeat(2, 1fr)',
    padding: '20px 24px',
    rowGap: '16px',
  },
});

const Stat = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  padding: '0 16px',
  borderLeft: '1px solid rgba(0,0,0,0.08)',
  '&:first-of-type': { borderLeft: 0, paddingLeft: 0 },
  '@media (max-width: 600px)': { borderLeft: 0, paddingLeft: 0 },
});

const StatNum = styled(Box)({
  fontSize: '28px',
  fontWeight: 600,
  letterSpacing: '-0.02em',
});

const StatLbl = styled(Box)({
  fontSize: '12px',
  color: '#86868B',
});

/* ─── Roster shape ───────────────────────────────────────────────── */

const Shape = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '16px',
  marginBottom: '48px',
  '@media (max-width: 800px)': { gridTemplateColumns: '1fr' },
});

const ShapeCard = styled(Box)({
  background: '#F5F5F7',
  borderRadius: '18px',
  padding: '24px',
});

const ShapeTitle = styled(Box)({
  fontSize: '15px',
  fontWeight: 600,
  letterSpacing: '-0.01em',
  marginBottom: '16px',
});

const PosRow = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '32px 1fr 56px',
  gap: '12px',
  alignItems: 'center',
  padding: '8px 0',
});

const PosCode = styled(Box)({
  fontSize: '11px',
  fontWeight: 700,
  color: '#6E6E73',
});

const Meter = styled(Box)({
  height: '6px',
  background: '#EBEBED',
  borderRadius: '3px',
  position: 'relative',
});

const PosLabel = styled(Box)({
  fontSize: '12px',
  color: '#86868B',
  textAlign: 'right',
});

/* ─── Build step (rosters) ───────────────────────────────────────── */

const PackageTabs = styled(Box)({
  display: 'flex',
  gap: '8px',
  overflowX: 'auto',
  marginBottom: '20px',
  paddingBottom: '4px',
});

const PTab = styled(Box)(({ active, isAdd }) => ({
  flex: '0 0 auto',
  padding: '10px 14px',
  background: isAdd ? '#000000' : active ? '#000000' : '#F5F5F7',
  color: isAdd ? '#FFFFFF' : active ? '#FFFFFF' : '#1D1D1F',
  borderRadius: '12px',
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  minWidth: isAdd ? 'auto' : '180px',
  transition: 'all 0.2s ease',
  fontWeight: isAdd ? 600 : 500,
  alignItems: isAdd ? 'center' : 'flex-start',
  justifyContent: isAdd ? 'center' : 'flex-start',
  boxShadow: isAdd ? '0 2px 8px rgba(0,0,0,0.12)' : 'none',
  '&:hover': {
    background: isAdd ? '#000000' : active ? '#000000' : '#EBEBED',
    transform: isAdd ? 'translateY(-1px)' : 'none',
  },
}));

const PTabName = styled(Box)({
  fontSize: '13px',
  fontWeight: 600,
  letterSpacing: '-0.01em',
});

const PTabState = styled(Box)({
  fontSize: '11px',
  opacity: 0.65,
});

const PTabSummary = styled(Box)({
  fontSize: '11px',
  opacity: 0.85,
  letterSpacing: '-0.01em',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: '180px',
});

const Rosters = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '16px',
  marginBottom: '32px',
  '@media (max-width: 800px)': { gridTemplateColumns: '1fr' },
});

const RosterCol = styled(Box)({
  background: '#F5F5F7',
  borderRadius: '18px',
  padding: '20px',
  display: 'flex',
  flexDirection: 'column',
});

const ColHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  marginBottom: '12px',
});

const ColWho = styled(Box)({
  fontSize: '15px',
  fontWeight: 600,
  letterSpacing: '-0.01em',
});

const ColShape = styled(Box)({
  fontSize: '11px',
  color: '#86868B',
});

const PosChips = styled(Box)({
  display: 'flex',
  gap: '5px',
  flexWrap: 'wrap',
  marginBottom: '12px',
});

const PChip = styled(Box)(({ active }) => ({
  fontSize: '11px',
  padding: '5px 11px',
  borderRadius: '980px',
  background: active ? '#000000' : '#FFFFFF',
  color: active ? '#FFFFFF' : '#6E6E73',
  cursor: 'pointer',
  fontWeight: 500,
}));

const PlayerList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  maxHeight: '420px',
  overflowY: 'auto',
});

const PlayerRow = styled(Box)(({ added }) => ({
  display: 'grid',
  gridTemplateColumns: '36px 1fr',
  gap: '12px',
  alignItems: 'center',
  padding: '8px 12px',
  borderRadius: '10px',
  background: added ? '#000000' : '#FFFFFF',
  color: added ? '#FFFFFF' : '#1D1D1F',
  cursor: 'pointer',
  '&:hover': { background: added ? '#000000' : '#FAFAFA' },
}));

const PlayerAvatar = styled(Box)(({ added }) => ({
  width: '36px',
  height: '36px',
  borderRadius: '50%',
  background: added ? 'rgba(255,255,255,0.18)' : '#F5F5F7',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '10px',
  fontWeight: 700,
  letterSpacing: '0.5px',
  color: added ? '#FFFFFF' : '#86868B',
  flex: '0 0 36px',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'top center',
  },
}));

const PlayerPos = styled(Box)(({ added }) => ({
  fontSize: '10px',
  fontWeight: 700,
  padding: '4px 6px',
  borderRadius: '5px',
  background: added ? 'rgba(255,255,255,0.15)' : '#EBEBED',
  textAlign: 'center',
}));

const PlayerName = styled(Box)({
  fontSize: '13px',
  fontWeight: 500,
  letterSpacing: '-0.01em',
});

const PlayerMeta = styled(Box)(({ added }) => ({
  fontSize: '11px',
  color: added ? 'rgba(255,255,255,0.6)' : '#86868B',
}));

/* ─── Build summary panel ────────────────────────────────────────── */

const BuildSummary = styled(Box)({
  background: '#F5F5F7',
  borderRadius: '18px',
  padding: '20px 24px',
  display: 'grid',
  gridTemplateColumns: '1fr auto 1fr',
  gap: '20px',
  alignItems: 'center',
  '@media (max-width: 800px)': { gridTemplateColumns: '1fr', gap: '16px' },
});

const FootSide = styled(Box)(({ align }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  textAlign: align || 'left',
  alignItems: align === 'right' ? 'flex-end' : 'flex-start',
}));

const FootLbl = styled(Box)({
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '1.2px',
  textTransform: 'uppercase',
  color: '#86868B',
});

const FootPills = styled(Box)({
  display: 'flex',
  gap: '6px',
  flexWrap: 'wrap',
});

const FootPill = styled(Box)({
  fontSize: '12px',
  padding: '5px 10px',
  background: '#FFFFFF',
  color: '#1D1D1F',
  borderRadius: '980px',
});

const Bal = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  minWidth: '220px',
  '@media (max-width: 800px)': { minWidth: 0, width: '100%' },
});

const BalNum = styled(Box)(({ tone }) => ({
  fontSize: '22px',
  fontWeight: 600,
  letterSpacing: '-0.01em',
  color: tone === 'fav' ? '#1A8754' : tone === 'unfav' ? '#C2342B' : '#1D1D1F',
}));

const BalBar = styled(Box)({
  width: '220px',
  height: '5px',
  background: '#FFFFFF',
  borderRadius: '3px',
  margin: '6px 0',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: '50%',
    width: '1px',
    background: 'rgba(0,0,0,0.15)',
  },
  '@media (max-width: 800px)': { width: '100%' },
});

const BalLbl = styled(Box)({
  fontSize: '11px',
  color: '#86868B',
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
});

const KtcLink = styled('a')({
  marginTop: '4px',
  fontSize: '11px',
  color: '#86868B',
  textDecoration: 'none',
  letterSpacing: '-0.01em',
  '&:hover': { color: '#1D1D1F', textDecoration: 'underline' },
});

const AddTradeRow = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  marginTop: '20px',
});

const AddTradeBtn = styled('button')({
  background: '#F5F5F7',
  color: '#1D1D1F',
  padding: '12px 22px',
  borderRadius: '980px',
  border: 0,
  fontSize: '14px',
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'inherit',
  transition: 'background 0.2s ease',
  '&:hover': { background: '#EBEBED' },
  '&:disabled': { color: '#86868B', cursor: 'not-allowed' },
});

/* ─── Send step ──────────────────────────────────────────────────── */

const TradeReviewCard = styled(Box)({
  background: '#F5F5F7',
  border: '1px solid #EBEBED',
  borderRadius: '18px',
  padding: '24px',
  marginBottom: '20px',
});

const TradeReviewHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  marginBottom: '16px',
});

const TradeReviewTitle = styled(Box)({
  fontSize: '13px',
  fontWeight: 600,
  letterSpacing: '1.2px',
  textTransform: 'uppercase',
  color: '#86868B',
});

const TradeReviewBalance = styled(Box)(({ tone }) => ({
  fontSize: '14px',
  fontWeight: 600,
  color:
    tone === 'fav'
      ? '#34C759'
      : tone === 'unfav'
      ? '#FF3B30'
      : '#1D1D1F',
}));

const TradeReviewSides = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '1fr auto 1fr',
  gap: '20px',
  alignItems: 'center',
  '@media (max-width: 600px)': {
    gridTemplateColumns: '1fr',
    gap: '12px',
  },
});

const SideBlock = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
});

const SideArrow = styled(Box)({
  fontSize: '18px',
  color: '#86868B',
  textAlign: 'center',
  '@media (max-width: 600px)': { display: 'none' },
});

const TradeRemove = styled('button')({
  background: 'transparent',
  border: 0,
  fontSize: '12px',
  color: '#86868B',
  cursor: 'pointer',
  padding: '4px 8px',
  fontFamily: 'inherit',
  '&:hover': { color: '#FF3B30' },
});

/* ─── Sticky bottom nav ──────────────────────────────────────────── */

const StickyNav = styled(Box)({
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: 40,
  background: 'rgba(255,255,255,0.95)',
  backdropFilter: 'blur(24px)',
  borderTop: '1px solid #EBEBED',
  padding: '16px 24px 20px',
});

const StickyInner = styled(Box)({
  maxWidth: '1080px',
  margin: '0 auto',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '16px',
});

const StickyHint = styled(Box)({
  fontSize: '13px',
  color: '#6E6E73',
});

const NavBtns = styled(Box)({
  display: 'flex',
  gap: '12px',
});

const Pill = styled('button')(({ variant }) => ({
  padding: '14px 26px',
  borderRadius: '980px',
  border: 0,
  fontSize: '15px',
  fontWeight: 500,
  letterSpacing: '-0.01em',
  cursor: 'pointer',
  fontFamily: 'inherit',
  transition: 'all 0.2s ease',
  background: variant === 'primary' ? '#1D1D1F' : '#F5F5F7',
  color: variant === 'primary' ? '#FFFFFF' : '#1D1D1F',
  '&:hover': {
    transform: 'translateY(-1px)',
    background: variant === 'primary' ? '#1D1D1F' : '#EBEBED',
  },
  '&:disabled': {
    background: '#F5F5F7',
    color: '#86868B',
    transform: 'none',
    cursor: 'not-allowed',
    opacity: 1,
  },
}));

const Loading = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '60vh',
});

const ErrorState = styled(Box)({
  textAlign: 'center',
  padding: '80px 20px',
  color: '#6E6E73',
});

/* ─── Helpers ────────────────────────────────────────────────────── */

const sleeperPlayerThumb = (sleeperId) =>
  sleeperId ? `https://sleepercdn.com/content/nfl/players/thumb/${sleeperId}.jpg` : null;

// Avatar that loads the player's Sleeper thumbnail and falls back to position text
// on 404 / broken image. Image_url in the DB is unreliable so we always construct.
const PlayerAvatarImg = ({ sleeperId, fallback, added }) => {
  const [errored, setErrored] = useState(false);
  const url = sleeperPlayerThumb(sleeperId);
  if (!url || errored) {
    return <PlayerAvatar added={added}>{fallback}</PlayerAvatar>;
  }
  return (
    <PlayerAvatar added={added}>
      <img src={url} alt="" onError={() => setErrored(true)} />
    </PlayerAvatar>
  );
};

const sortPlayers = (players) =>
  [...players].sort((a, b) => {
    const posA = POSITION_ORDER[a.position] ?? 999;
    const posB = POSITION_ORDER[b.position] ?? 999;
    if (posA !== posB) return posA - posB;
    if (a.position === 'PICK' && b.position === 'PICK') {
      const yearA = parseInt(a.season) || 0;
      const yearB = parseInt(b.season) || 0;
      if (yearA !== yearB) return yearA - yearB;
      return (a.round || 0) - (b.round || 0);
    }
    return (b.ktcValue ?? 0) - (a.ktcValue ?? 0);
  });

const filterRoster = (roster, filter) => {
  if (filter === 'ALL') return sortPlayers(roster);
  const positionFilter = filter === 'PICKS' ? 'PICK' : filter;
  return sortPlayers(roster.filter((p) => p.position === positionFilter));
};

const summarizeSide = (side) => {
  if (!side || side.length === 0) return '—';
  return side.map((p) => p.name).join(', ');
};

const computeBalance = (yourSide, theirSide) => {
  // yourSide = what the user GIVES; theirSide = what the user GETS.
  // youWin = user receives more value than they send.
  const yourTotal = yourSide.reduce((s, p) => s + (p.ktcValue || 0), 0);
  const theirTotal = theirSide.reduce((s, p) => s + (p.ktcValue || 0), 0);
  if (yourTotal === 0 && theirTotal === 0) {
    return { label: 'Build trade', sub: '', tone: 'neutral', pct: 0, dir: 'right' };
  }
  if (yourTotal === 0 || theirTotal === 0) {
    return { label: 'Add to both sides', sub: '', tone: 'neutral', pct: 0, dir: 'right' };
  }
  // 3-for-1 (or wider) trades break the additive model — KTC values don't combine
  // linearly because the consolidating side is typically getting the elite asset.
  const maxCount = Math.max(yourSide.length, theirSide.length);
  const minCount = Math.min(yourSide.length, theirSide.length);
  if (maxCount >= 3 && minCount === 1) {
    return {
      label: 'Cheeky move',
      sub: 'Hard to value precisely',
      tone: 'neutral',
      pct: 0,
      dir: 'right',
    };
  }
  const diff = Math.abs(yourTotal - theirTotal);
  const larger = Math.max(yourTotal, theirTotal);
  const percentDiff = (diff / larger) * 100;
  const youWin = theirTotal > yourTotal;
  // Bar fills toward the side that's "up": right = "You get", left = "You give".
  const dir = youWin ? 'right' : 'left';
  const pct = Math.min(50, percentDiff);
  if (percentDiff < 5) return { label: 'Even', sub: 'Perfectly balanced', tone: 'fav', pct, dir };
  if (percentDiff < 10) return { label: 'Fair', sub: 'Well balanced', tone: 'fav', pct, dir };
  if (percentDiff < 15)
    return {
      label: youWin ? 'Slightly favors you' : 'Slightly favors them',
      sub: 'Close call',
      tone: 'fav',
      pct,
      dir,
    };
  if (percentDiff < 25)
    return {
      label: youWin ? 'Favors you' : 'Favors them',
      sub: 'Notable gap',
      tone: youWin ? 'fav' : 'unfav',
      pct,
      dir,
    };
  return {
    label: youWin ? 'Favors you' : 'Favors them',
    sub: 'Significant gap',
    tone: youWin ? 'fav' : 'unfav',
    pct,
    dir,
  };
};

// Map Sleeper's bonus_rec_te decimal to KTC's tep param.
// 0 -> 0 (none), 0.5 -> 1, 1.0 -> 2, 1.5 -> 3
const tepParamForBonus = (bonus) => {
  const map = { 0: '0', 0.5: '1', 1: '2', 1.5: '3' };
  return map[bonus] != null ? map[bonus] : '0';
};

const generateKTCLink = (yourSide, theirSide, isSuperFlex, tePremium, hasTEPremium) => {
  const yourKtcIds = yourSide.map((p) => p.ktcId).filter(Boolean);
  const theirKtcIds = theirSide.map((p) => p.ktcId).filter(Boolean);
  const teamOne = yourKtcIds.join('|');
  const teamTwo = theirKtcIds.join('|');
  const format = isSuperFlex ? 2 : 1;
  // Prefer the granular bonus value when we have it. Fall back to the
  // boolean (legacy callers / leagues without bonus_rec_te populated).
  const tep =
    typeof tePremium === 'number' && tePremium > 0
      ? tepParamForBonus(tePremium)
      : hasTEPremium
        ? '1'
        : '0';
  if (!teamOne && !teamTwo) return 'https://keeptradecut.com/dynasty-rankings';
  return `https://keeptradecut.com/trade-calculator?var=5&pickVal=0&teamOne=${teamOne}&teamTwo=${teamTwo}&format=${format}&isStartup=0&tep=${tep}`;
};


/* ─── 2H DARK BUILD STEP ─────────────────────────────────────────── *
 * Self-contained dark layout for the build step (matches mockups/canvas-v2/option-2h-columns.html).
 * Three-column grid: roster · build canvas · roster. Top bar dark, bottom bar dark.
 * Other steps (signin / send) keep the existing light layout.
 */

// Don't forward design-time props to the DOM (active, added, match, align, on, side, tone, variant).
const noForward = (skip) => ({
  shouldForwardProp: (prop) => !skip.includes(prop),
});

const sleeperUserAvatar = (avatar) =>
  avatar ? `https://sleepercdn.com/avatars/thumbs/${avatar}` : null;

// Smart user avatar — shows Sleeper avatar img, falls back to letter initial.
// `tone` controls the fallback bg: "you" = neutral grey, "them" = orange (default).
const UserAvatarImg = ({ avatar, fallback, size = 28, tone = 'them', className }) => {
  const [errored, setErrored] = useState(false);
  const url = sleeperUserAvatar(avatar);
  return (
    <Box
      className={className}
      sx={{
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: '50%',
        background:
          tone === 'you'
            ? '#EBEBED'
            : 'linear-gradient(135deg, #FF6B35, #FF9F1C)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: tone === 'you' ? '#6E6E73' : '#FFFFFF',
        fontWeight: 700,
        fontSize: size <= 28 ? '12px' : size <= 32 ? '13px' : `${Math.round(size * 0.4)}px`,
        flex: `0 0 ${size}px`,
        overflow: 'hidden',
        '& img': { width: '100%', height: '100%', objectFit: 'cover' },
      }}
    >
      {url && !errored ? (
        <img src={url} alt="" onError={() => setErrored(true)} />
      ) : (
        (fallback || '?').toString().toUpperCase()[0]
      )}
    </Box>
  );
};

const DarkPage = styled(Box)({
  height: 'calc(100vh - 60px)', /* below the shell topbar */
  background: '#FFFFFF',
  color: '#1D1D1F',
  WebkitFontSmoothing: 'antialiased',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  '@media (max-width: 800px)': {
    height: 'auto',
    minHeight: 'calc(100vh - 60px)',
    overflow: 'visible',
  },
});

const DarkTopBar = styled(Box)({
  height: '56px',
  background: '#FFFFFF',
  backdropFilter: 'blur(16px)',
  borderBottom: '1px solid #EBEBED',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 24px',
  flex: '0 0 56px',
  position: 'relative',
  '@media (max-width: 800px)': {
    height: '52px',
    flex: '0 0 52px',
    padding: '0 12px',
    position: 'sticky',
    top: 0,
    zIndex: 50,
  },
});
const DarkTopBarLogo = styled('img')({
  position: 'absolute',
  left: '50%',
  top: '50%',
  transform: 'translate(-50%, -50%)',
  height: '28px',
  width: 'auto',
  // Light shell: the asset is the white logo — invert it to dark so it reads
  // on the white top bar (style-only conversion; src stays untouched).
  filter: 'invert(1)',
  cursor: 'pointer',
  transition: 'transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
  '&:hover': { transform: 'translate(-50%, calc(-50% - 2px))' },
  // Tablet range still shows the "FANTASY TACO" wordmark in the left group,
  // so the centered logo would clash. On phone the wordmark hides, leaving
  // the center free for the mark.
  '@media (max-width: 800px)': { display: 'none' },
  '@media (max-width: 600px)': { display: 'block', height: '24px' },
});

const DarkBrand = styled(Box)({
  fontSize: '13px',
  fontWeight: 500,
  letterSpacing: '2px',
  textTransform: 'uppercase',
  color: '#86868B',
  '@media (max-width: 600px)': { display: 'none' },
});

const DarkStepperRow = styled(Box)({ display: 'flex', alignItems: 'center', gap: '10px' });
const DarkStepEntry = styled(Box, noForward(['active']))(({ active }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  fontSize: '12px',
  color: active ? '#1D1D1F' : '#86868B',
  fontWeight: active ? 500 : 400,
  letterSpacing: '-0.01em',
  cursor: 'pointer',
}));
const DarkStepDot = styled(Box, noForward(['active']))(({ active }) => ({
  width: '18px',
  height: '18px',
  borderRadius: '50%',
  background: active ? '#1D1D1F' : '#F5F5F7',
  color: active ? '#FFFFFF' : '#86868B',
  fontSize: '11px',
  fontWeight: 600,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
}));
const DarkStepLine = styled(Box)({ width: '16px', height: '1px', background: '#EBEBED' });

const DarkScoutBtn = styled('a')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '10px',
  height: '36px',
  padding: '0 14px 0 6px',
  background: '#F5F5F7',
  borderRadius: '980px',
  color: '#1D1D1F',
  fontSize: '13px',
  fontWeight: 500,
  letterSpacing: '-0.01em',
  cursor: 'pointer',
  border: 0,
  fontFamily: 'inherit',
  textDecoration: 'none',
  transition: 'background 0.2s ease',
  '&:hover': { background: '#EBEBED' },
});
const DarkScoutMeta = styled('span')({ color: '#86868B', fontWeight: 400 });
const DarkScoutArrow = styled('span')({ color: '#86868B' });

const DarkStage = styled(Box)({
  flex: '1 1 auto',
  display: 'grid',
  gridTemplateColumns: '300px 1fr 300px',
  gridTemplateRows: '1fr auto',
  gridTemplateAreas: `
    "left center right"
    "left bottom right"
  `,
  overflow: 'hidden',
  background: '#FFFFFF',
  '@media (max-width: 800px)': {
    display: 'flex',
    flexDirection: 'column',
    gridTemplateColumns: 'none',
    gridTemplateRows: 'none',
    gridTemplateAreas: 'none',
    overflow: 'visible',
  },
});

const DarkRosterPane = styled(Box, noForward(['side', 'mobileHidden']))(({ side, mobileHidden }) => ({
  gridArea: side,
  background: '#FFFFFF',
  color: '#1D1D1F',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  borderRight: side === 'left' ? '1px solid #EBEBED' : 'none',
  borderLeft: side === 'right' ? '1px solid #EBEBED' : 'none',
  '@media (max-width: 800px)': {
    order: 3,
    borderLeft: 0,
    borderRight: 0,
    borderTop: '1px solid #EBEBED',
    overflow: 'visible',
    display: mobileHidden ? 'none' : 'flex',
  },
}));

const DarkRosterHead = styled(Box)({
  padding: '18px 18px 12px',
  borderBottom: '1px solid #EBEBED',
  '@media (max-width: 800px)': {
    padding: '12px 14px 10px',
  },
});

const DarkRosterTitleRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  marginBottom: '14px',
  '@media (max-width: 800px)': { marginBottom: '10px' },
});

const DarkRosterName = styled(Box)({
  fontSize: '14px',
  fontWeight: 600,
  letterSpacing: '-0.01em',
  color: '#1D1D1F',
  lineHeight: 1.2,
});
const DarkRosterSub = styled(Box)({
  fontSize: '11px',
  color: '#86868B',
  letterSpacing: '-0.01em',
  marginTop: '1px',
});

const DarkPosTabs = styled(Box)({
  display: 'flex',
  gap: '3px',
  flexWrap: 'nowrap',
  overflowX: 'auto',
  // Hide scrollbar — the row is short enough to drag/scroll without one.
  '&::-webkit-scrollbar': { display: 'none' },
  scrollbarWidth: 'none',
});
const DarkPos = styled(Box, noForward(['active']))(({ active }) => ({
  height: '24px',
  padding: '0 7px',
  borderRadius: '980px',
  background: active ? '#1D1D1F' : '#F5F5F7',
  color: active ? '#FFFFFF' : '#6E6E73',
  fontSize: '11px',
  fontWeight: 500,
  display: 'inline-flex',
  alignItems: 'center',
  gap: '3px',
  cursor: 'pointer',
  flex: '0 0 auto',
  transition: 'background 0.2s ease',
  whiteSpace: 'nowrap',
  '&:hover': { background: active ? '#1D1D1F' : '#EBEBED' },
  '& .ct': { opacity: 0.45, fontSize: '10px', fontWeight: 400 },
}));

const DarkPlayerList = styled(Box)({
  flex: '1 1 auto',
  overflowY: 'auto',
  padding: '10px 10px 24px',
  '@media (max-width: 800px)': {
    overflowY: 'visible',
    padding: '8px 8px 16px',
  },
});
const DarkPlayer = styled(Box, noForward(['added']))(({ added }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '9px 12px',
  borderRadius: '12px',
  cursor: 'pointer',
  transition: 'background 0.15s ease',
  marginBottom: '2px',
  background: added ? '#1D1D1F' : 'transparent',
  color: added ? '#FFFFFF' : '#1D1D1F',
  '&:hover': { background: added ? '#1D1D1F' : '#FAFAFA' },
}));
const DarkPlayerFace = styled(Box, noForward(['added']))(({ added }) => ({
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  background: added ? 'rgba(255,255,255,0.18)' : '#F5F5F7',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '10px',
  fontWeight: 700,
  color: added ? '#FFFFFF' : '#86868B',
  flex: '0 0 32px',
  overflow: 'hidden',
  '& img': { width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center' },
}));
const DarkPlayerName = styled(Box)({
  fontSize: '13px',
  fontWeight: 500,
  letterSpacing: '-0.01em',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});
const DarkPlayerMeta = styled(Box, noForward(['added']))(({ added }) => ({
  fontSize: '11px',
  color: added ? 'rgba(255,255,255,0.6)' : '#86868B',
  marginTop: '1px',
}));

// Player avatar for the dark layout — same logic as PlayerAvatarImg but uses DarkPlayerFace.
const DarkPlayerAvatarImg = ({ sleeperId, fallback, added }) => {
  const [errored, setErrored] = useState(false);
  const url = sleeperPlayerThumb(sleeperId);
  if (!url || errored) return <DarkPlayerFace added={added}>{fallback}</DarkPlayerFace>;
  return (
    <DarkPlayerFace added={added}>
      <img src={url} alt="" onError={() => setErrored(true)} />
    </DarkPlayerFace>
  );
};

const DarkCenterStage = styled(Box)({
  gridArea: 'center',
  overflowY: 'auto',
  padding: '36px 24px 24px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '32px',
  background: '#FFFFFF',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background:
      'radial-gradient(ellipse at center, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0) 60%)',
    pointerEvents: 'none',
  },
  '@media (max-width: 800px)': {
    order: 1,
    overflowY: 'visible',
    padding: '20px 14px 24px',
    gap: '24px',
  },
});

const DarkTradeCard = styled(Box)({
  position: 'relative',
  width: '100%',
  maxWidth: '540px',
  // Designed exception: the trade-building hero card stays dark on the light page.
  background: '#1D1D1F',
  border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: '16px',
  padding: '24px 26px 22px',
  boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
  '@media (max-width: 600px)': {
    padding: '18px 16px 16px',
    borderRadius: '16px',
  },
});
const DarkTradeNum = styled(Box)({
  fontSize: '11px',
  fontWeight: 700,
  letterSpacing: '1.6px',
  textTransform: 'uppercase',
  color: 'rgba(255,255,255,0.5)',
  textAlign: 'center',
  marginBottom: '22px',
  '& b': { color: '#FFFFFF', fontWeight: 700 },
  '@media (max-width: 600px)': { marginBottom: '16px' },
});

const DarkReceiveGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '1fr auto 1fr',
  gap: '18px',
  alignItems: 'stretch',
  '@media (max-width: 600px)': { gap: '8px' },
});
const DarkReceiveCol = styled(Box, noForward(['align']))(({ align }) => ({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
}));
const DarkReceiveHead = styled(Box, noForward(['align']))(({ align }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  paddingBottom: '10px',
  marginBottom: '12px',
  borderBottom: '1px solid rgba(255,255,255,0.06)',
  flexDirection: align === 'right' ? 'row-reverse' : 'row',
  justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
  '@media (max-width: 600px)': {
    gap: '6px',
    paddingBottom: '8px',
    marginBottom: '10px',
  },
}));
const DarkReceiveWho = styled(Box)({ minWidth: 0 });
const DarkReceiveName = styled(Box)({
  fontSize: '13px',
  fontWeight: 600,
  letterSpacing: '-0.01em',
  color: '#FFFFFF',
  lineHeight: 1.1,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  '@media (max-width: 600px)': { fontSize: '12px' },
});
const DarkReceiveLabel = styled(Box)({
  fontSize: '10px',
  fontWeight: 600,
  letterSpacing: '1.2px',
  textTransform: 'uppercase',
  color: 'rgba(255,255,255,0.45)',
  marginTop: '2px',
});

const DarkPillStack = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  flex: '1 1 auto',
});
const DarkPill = styled(Box, noForward(['align']))(({ align }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px 10px',
  borderRadius: '11px',
  background: 'rgba(255,255,255,0.06)',
  color: '#FFFFFF',
  fontSize: '13px',
  fontWeight: 500,
  flexDirection: align === 'right' ? 'row-reverse' : 'row',
  textAlign: align === 'right' ? 'right' : 'left',
  '@media (max-width: 600px)': {
    fontSize: '12px',
    padding: '7px 8px',
    gap: '6px',
    borderRadius: '10px',
  },
}));
const DarkPillFace = styled(Box)({
  width: '22px',
  height: '22px',
  borderRadius: '50%',
  background: 'rgba(255,255,255,0.12)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '9px',
  fontWeight: 700,
  color: 'rgba(255,255,255,0.6)',
  flex: '0 0 22px',
  overflow: 'hidden',
  '& img': { width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center' },
});
const DarkPillName = styled(Box)({
  flex: '1 1 auto',
  minWidth: 0,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});
const DarkPillX = styled(Box)({
  color: 'rgba(255,255,255,0.3)',
  cursor: 'pointer',
  fontSize: '14px',
});
const DarkPillEmpty = styled(Box)({
  border: '1px dashed rgba(255,255,255,0.10)',
  color: 'rgba(255,255,255,0.3)',
  fontStyle: 'italic',
  fontSize: '12px',
  padding: '8px 10px',
  borderRadius: '11px',
  textAlign: 'center',
  '@media (max-width: 600px)': {
    fontSize: '11px',
    padding: '8px 6px',
    lineHeight: 1.3,
  },
});

const DarkSwapDivider = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '28px',
  color: 'rgba(255,255,255,0.2)',
  fontSize: '16px',
  '@media (max-width: 600px)': {
    width: '14px',
    fontSize: '12px',
  },
});

// Pill avatar that swaps to Sleeper player image with fallback.
const DarkPillFaceImg = ({ sleeperId, fallback }) => {
  const [errored, setErrored] = useState(false);
  const url = sleeperPlayerThumb(sleeperId);
  if (!url || errored) return <DarkPillFace>{fallback}</DarkPillFace>;
  return (
    <DarkPillFace>
      <img src={url} alt="" onError={() => setErrored(true)} />
    </DarkPillFace>
  );
};

const DarkKtcLink = styled('a')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
  marginTop: '18px',
  paddingTop: '16px',
  borderTop: '1px solid rgba(255,255,255,0.06)',
  color: 'rgba(255,255,255,0.5)',
  fontSize: '12px',
  fontWeight: 500,
  letterSpacing: '-0.01em',
  textDecoration: 'none',
  transition: 'color 0.2s ease',
  '&:hover': { color: '#FFFFFF' },
  '@media (max-width: 600px)': {
    marginTop: '14px',
    paddingTop: '12px',
    fontSize: '11px',
  },
});

/* Trade Intel section — title, tabs, sub-filters, cards */
const DarkIntel = styled(Box)({ width: '100%', maxWidth: '760px' });
const DarkIntelHead = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: '16px',
  marginBottom: '16px',
  padding: '0 8px',
  '@media (max-width: 600px)': {
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '12px',
    padding: '0 4px',
  },
});
const DarkIntelTitle = styled(Box)({
  fontSize: '18px',
  fontWeight: 600,
  letterSpacing: '-0.02em',
  color: '#1D1D1F',
  lineHeight: 1.2,
  marginBottom: '4px',
  '@media (max-width: 600px)': { fontSize: '17px' },
});
const DarkIntelSub = styled(Box)({
  fontSize: '13px',
  color: '#6E6E73',
  letterSpacing: '-0.01em',
  lineHeight: 1.4,
  '& b': { color: '#1D1D1F', fontWeight: 500 },
});

const DarkIntelTabsRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '14px',
  padding: '0 8px',
  gap: '12px',
  flexWrap: 'wrap',
  '@media (max-width: 600px)': {
    padding: '0 4px',
    gap: '8px',
    marginBottom: '12px',
  },
});
const DarkIntelTabs = styled(Box)({
  display: 'inline-flex',
  gap: '2px',
  background: '#F5F5F7',
  borderRadius: '980px',
  padding: '3px',
});
const DarkIntelTab = styled('button', noForward(['active']))(({ active }) => ({
  height: '30px',
  padding: '0 14px',
  border: 0,
  background: active ? '#1D1D1F' : 'transparent',
  color: active ? '#FFFFFF' : '#86868B',
  borderRadius: '980px',
  fontFamily: 'inherit',
  fontSize: '12px',
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  '& .ct': {
    background: active ? 'rgba(255,255,255,0.18)' : '#EBEBED',
    color: active ? 'rgba(255,255,255,0.85)' : '#86868B',
    fontSize: '10px',
    fontWeight: 700,
    padding: '1px 6px',
    borderRadius: '999px',
  },
}));

const DarkAttachToggle = styled('label')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '10px',
  padding: '6px 14px 6px 6px',
  background: '#F5F5F7',
  border: '1px solid #EBEBED',
  borderRadius: '980px',
  cursor: 'pointer',
});
const DarkAttachSwitch = styled(Box, noForward(['on']))(({ on }) => ({
  width: '32px',
  height: '18px',
  borderRadius: '999px',
  background: on ? '#000000' : '#D2D2D7',
  position: 'relative',
  flex: '0 0 32px',
  transition: 'background 0.2s ease',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '2px',
    right: on ? '2px' : 'auto',
    left: on ? 'auto' : '2px',
    width: '14px',
    height: '14px',
    borderRadius: '50%',
    background: '#FFFFFF',
    transition: 'all 0.2s ease',
  },
}));
const DarkAttachLabel = styled(Box)({
  fontSize: '12px',
  color: '#1D1D1F',
  fontWeight: 500,
  letterSpacing: '-0.01em',
  '& .sub': {
    display: 'block',
    fontSize: '11px',
    fontWeight: 400,
    color: '#6E6E73',
    marginTop: '1px',
  },
  '@media (max-width: 600px)': {
    '& .sub': { display: 'none' },
  },
});
const DarkTradesInfo = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  padding: '6px 12px',
  background: 'transparent',
  border: '1px dashed #D2D2D7',
  borderRadius: '980px',
  fontSize: '11px',
  color: '#6E6E73',
  letterSpacing: '-0.01em',
});

const DarkSubFilters = styled(Box)({
  display: 'flex',
  gap: '6px',
  flexWrap: 'wrap',
  marginBottom: '14px',
  padding: '0 8px',
  '@media (max-width: 600px)': {
    flexWrap: 'nowrap',
    overflowX: 'auto',
    padding: '0 4px 4px',
    marginBottom: '12px',
    '&::-webkit-scrollbar': { display: 'none' },
    scrollbarWidth: 'none',
    '& > *': { flex: '0 0 auto' },
  },
});
const DarkSubFilter = styled('button', noForward(['active']))(({ active }) => ({
  height: '28px',
  padding: '0 12px',
  borderRadius: '980px',
  background: active ? '#1D1D1F' : 'transparent',
  border: '1px solid #EBEBED',
  borderColor: active ? '#1D1D1F' : '#EBEBED',
  color: active ? '#FFFFFF' : '#6E6E73',
  fontFamily: 'inherit',
  fontSize: '12px',
  fontWeight: 500,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  '&:hover': { borderColor: active ? '#1D1D1F' : '#D2D2D7', color: active ? '#FFFFFF' : '#1D1D1F' },
  '& .ct': {
    background: active ? 'rgba(255,255,255,0.18)' : '#F5F5F7',
    fontSize: '10px',
    fontWeight: 600,
    padding: '1px 6px',
    borderRadius: '999px',
    color: active ? 'rgba(255,255,255,0.85)' : '#86868B',
  },
}));

const DarkIntelCards = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '12px',
  overflowX: 'auto',
  padding: '4px 8px 16px',
  scrollSnapType: 'x mandatory',
  '&::-webkit-scrollbar': { height: 0 },
  '@media (max-width: 600px)': {
    padding: '4px 4px 16px',
    gap: '10px',
  },
});
const DarkDealCard = styled(Box)({
  flex: '0 0 420px',
  maxHeight: '440px',
  background: '#F5F5F7',
  border: '1px solid #EBEBED',
  borderRadius: '16px',
  padding: '18px',
  transition: 'all 0.2s ease',
  scrollSnapAlign: 'start',
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
  '&:hover': { background: '#EBEBED', transform: 'translateY(-2px)' },
  '@media (max-width: 600px)': {
    flex: '0 0 88%',
    maxHeight: '460px',
    padding: '16px',
    borderRadius: '14px',
  },
});

// Scrollable body — two columns side-by-side, counterparty left, subject right.
const DarkDealBody = styled(Box)({
  flex: '1 1 auto',
  overflowY: 'auto',
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '14px',
  alignItems: 'start',
  margin: '0 -4px',
  padding: '0 4px',
  '@media (max-width: 600px)': {
    gap: '10px',
  },
  // Subtle scrollbar.
  '&::-webkit-scrollbar': { width: '4px' },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(0,0,0,0.12)',
    borderRadius: '2px',
  },
  '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(0,0,0,0.2)' },
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(0,0,0,0.12) transparent',
});
const DarkDealHead = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: '12px',
  marginBottom: '14px',
  paddingBottom: '12px',
  borderBottom: '1px solid #EBEBED',
  '@media (max-width: 600px)': {
    flexWrap: 'wrap',
    gap: '8px',
    marginBottom: '10px',
    paddingBottom: '10px',
  },
});
const DarkDealSettings = styled(Box)({
  marginTop: '4px',
  fontSize: '11px',
  fontWeight: 400,
  color: '#86868B',
  letterSpacing: '-0.01em',
});
const DarkDealWhen = styled(Box)({
  fontSize: '13px',
  fontWeight: 600,
  color: '#1D1D1F',
  letterSpacing: '-0.01em',
  '& .league': { color: '#86868B', fontWeight: 400, marginLeft: '4px' },
});
const DarkDealRosterLink = styled('a')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  padding: '4px 10px',
  borderRadius: '980px',
  background: '#FFFFFF',
  color: '#1D1D1F',
  fontSize: '11px',
  fontWeight: 500,
  letterSpacing: '-0.01em',
  textDecoration: 'none',
  whiteSpace: 'nowrap',
  transition: 'all 0.2s ease',
  '&:hover': {
    background: '#EBEBED',
    color: '#1D1D1F',
  },
});
// Each "party" block: user @name on top, "received" label, then a single
// vertical column of pills. Two parties stacked per card = clearer than
// give/got because each side shows what THEY took home.
const DarkDealParty = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  marginBottom: '12px',
  '&:last-child': { marginBottom: 0 },
});
const DarkDealPartyName = styled(Box)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#1D1D1F',
  letterSpacing: '-0.01em',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});
const DarkDealPartyLabel = styled(Box)({
  fontSize: '10px',
  fontWeight: 700,
  letterSpacing: '1.4px',
  textTransform: 'uppercase',
  color: '#86868B',
  marginTop: '3px',
  marginBottom: '10px',
});
const DarkDealPills = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
});
const DarkDealPill = styled(Box, noForward(['match']))(({ match }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '8px 12px',
  background: match ? '#1D1D1F' : '#FFFFFF',
  border: match ? '1px solid #1D1D1F' : '1px solid transparent',
  borderRadius: '10px',
  fontSize: '13px',
  fontWeight: 500,
  letterSpacing: '-0.01em',
  color: match ? '#FFFFFF' : '#1D1D1F',
  '@media (max-width: 600px)': {
    fontSize: '12px',
    padding: '7px 9px',
    gap: '7px',
  },
  '& .face': {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    background: match ? 'rgba(255,255,255,0.18)' : '#F5F5F7',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '9px',
    fontWeight: 700,
    color: match ? '#FFFFFF' : '#86868B',
    flex: '0 0 24px',
    overflow: 'hidden',
  },
  '& .face img': { width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center' },
}));
// Subtle horizontal rule between the two parties — visual breath.
const DarkDealRule = styled(Box)({
  height: '1px',
  background: '#EBEBED',
  margin: '0 0 12px',
});

/* Tweet card (dark) */
// Tweet cards link out to the original on X (per X display requirements:
// must show avatar + name + handle + timestamp, and link to the source).
const DarkTweetCard = styled('a', noForward(['included']))(({ included }) => ({
  flex: '0 0 320px',
  background: '#F5F5F7',
  border: included ? '1px solid #1D1D1F' : '1px solid #EBEBED',
  borderRadius: '16px',
  padding: '18px',
  transition: 'all 0.2s ease',
  scrollSnapAlign: 'start',
  cursor: 'pointer',
  opacity: included ? 1 : 0.45,
  textDecoration: 'none',
  color: 'inherit',
  display: 'flex',
  flexDirection: 'column',
  '&:hover': { background: '#EBEBED', transform: 'translateY(-2px)' },
  '@media (max-width: 600px)': {
    flex: '0 0 86%',
    padding: '16px',
    borderRadius: '14px',
  },
}));
const DarkTweetRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '12px',
});
const DarkTweetAv = styled(Box)({
  width: '40px',
  height: '40px',
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #1DA1F2, #0E71B8)',
  flex: '0 0 40px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '14px',
  fontWeight: 700,
  color: '#FFFFFF',
  overflow: 'hidden',
  '& img': { width: '100%', height: '100%', objectFit: 'cover' },
});
const DarkTweetWho = styled(Box)({ minWidth: 0, flex: '1 1 auto' });
const DarkTweetName = styled(Box)({
  fontSize: '14px',
  fontWeight: 600,
  letterSpacing: '-0.01em',
  lineHeight: 1.2,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});
const DarkTweetHandle = styled(Box)({
  fontSize: '13px',
  color: '#86868B',
  marginTop: '2px',
});
// "View on X" affordance — required by X display rules.
const DarkTweetXMark = styled(Box)({
  width: '24px',
  height: '24px',
  borderRadius: '50%',
  background: '#FFFFFF',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '12px',
  fontWeight: 700,
  color: '#6E6E73',
  flex: '0 0 24px',
  letterSpacing: 0,
});
const DarkTweetText = styled(Box)({
  fontSize: '15px',
  color: '#1D1D1F',
  lineHeight: 1.45,
  letterSpacing: '-0.005em',
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitLineClamp: 6,
  WebkitBoxOrient: 'vertical',
  marginBottom: '12px',
});
const DarkTweetFoot = styled(Box)({
  marginTop: 'auto',
  paddingTop: '12px',
  borderTop: '1px solid #EBEBED',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  fontSize: '12px',
  color: '#86868B',
  letterSpacing: '-0.01em',
});
const DarkIntelEmpty = styled(Box)({
  textAlign: 'center',
  padding: '36px 16px',
  color: '#86868B',
  fontSize: '13px',
  letterSpacing: '-0.01em',
  width: '100%',
});

/* Bottom send bar */
const DarkBottom = styled(Box)({
  gridArea: 'bottom',
  background: 'rgba(255,255,255,0.95)',
  borderTop: '1px solid #EBEBED',
  padding: '14px 24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '16px',
  '@media (max-width: 800px)': {
    order: 4,
    position: 'sticky',
    bottom: 0,
    zIndex: 30,
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: '10px',
    padding: '10px 12px calc(env(safe-area-inset-bottom, 0px) + 10px)',
    background: 'rgba(255,255,255,0.95)',
    backdropFilter: 'blur(20px)',
  },
});
const DarkBottomLeft = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '16px',
  fontSize: '13px',
  color: '#6E6E73',
  letterSpacing: '-0.01em',
  '& b': { color: '#1D1D1F', fontWeight: 600 },
  '@media (max-width: 800px)': {
    fontSize: '12px',
    gap: '10px',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    width: '100%',
  },
});
const DarkReviewLink = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  height: '32px',
  padding: '0 14px',
  borderRadius: '980px',
  background: '#F5F5F7',
  color: '#1D1D1F',
  border: 0,
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: '12px',
  fontWeight: 500,
  textDecoration: 'none',
  whiteSpace: 'nowrap',
  flexShrink: 0,
  transition: 'background 0.2s ease',
  '&:hover': { background: '#EBEBED' },
});
const DarkDeckMini = styled(Box)({
  width: '16px',
  height: '22px',
  borderRadius: '3px',
  background: 'linear-gradient(135deg, #D2D2D7, #EBEBED)',
  border: '1px solid #D2D2D7',
  position: 'relative',
  '&::before, &::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    borderRadius: '3px',
    background: 'linear-gradient(135deg, #E8E8ED, #F5F5F7)',
    border: '1px solid #EBEBED',
    zIndex: -1,
  },
  '&::before': { transform: 'rotate(-7deg) translate(-3px, 1px)' },
  '&::after': { transform: 'rotate(7deg) translate(3px, 1px)', zIndex: -2 },
});
const DarkActions = styled(Box)({
  display: 'flex',
  gap: '10px',
  '@media (max-width: 800px)': {
    width: '100%',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px',
    // A lone action (e.g. verify's Done) takes the full row instead of half
    '& > :only-child': { gridColumn: '1 / -1' },
  },
});

/* Deck-review modal — horizontal slider of locked trades, opened from the
 * "X trades in deck · review →" link on the build view. */
const DarkDeckOverlay = styled(Box)({
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.4)',
  backdropFilter: 'blur(8px)',
  WebkitBackdropFilter: 'blur(8px)',
  zIndex: 1200,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '40px 16px',
  animation: 'deckFadeIn 0.2s ease',
  '@keyframes deckFadeIn': {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
  '@media (max-width: 600px)': { padding: 0 },
});
const DarkDeckPanel = styled(Box)({
  width: '100%',
  maxWidth: '1080px',
  maxHeight: '100%',
  background: '#FFFFFF',
  border: '1px solid #EBEBED',
  borderRadius: '24px',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
  '@media (max-width: 600px)': {
    height: '100%',
    maxHeight: '100%',
    borderRadius: 0,
    border: 0,
  },
});
const DarkDeckHead = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '20px 24px',
  borderBottom: '1px solid #EBEBED',
  '@media (max-width: 600px)': {
    padding: '14px 16px',
    paddingTop: 'calc(env(safe-area-inset-top, 0px) + 14px)',
  },
});
const DarkDeckTitle = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  minWidth: 0,
  '& .lbl': {
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '1.4px',
    textTransform: 'uppercase',
    color: '#86868B',
  },
  '& .h': {
    fontSize: '18px',
    fontWeight: 600,
    letterSpacing: '-0.01em',
    color: '#1D1D1F',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  '@media (max-width: 600px)': {
    '& .h': {
      fontSize: '15px',
      whiteSpace: 'nowrap',
    },
  },
});
const DarkDeckClose = styled('button')({
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  background: '#F5F5F7',
  border: 0,
  color: '#1D1D1F',
  fontSize: '16px',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: 'inherit',
  transition: 'background 0.2s ease',
  '&:hover': { background: '#EBEBED' },
});
const DarkDeckSlider = styled(Box)({
  flex: 1,
  minHeight: 0,
  display: 'flex',
  alignItems: 'stretch',
  gap: '24px',
  overflowX: 'auto',
  overflowY: 'hidden',
  padding: '24px 32px 20px',
  scrollSnapType: 'x mandatory',
  '&::-webkit-scrollbar': { height: '6px' },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(0,0,0,0.12)',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(0,0,0,0.2)' },
  // Mobile: stack trades vertically — the horizontal snap strip clips cards
  '@media (max-width: 600px)': {
    flexDirection: 'column',
    alignItems: 'stretch',
    overflowX: 'hidden',
    overflowY: 'auto',
    scrollSnapType: 'none',
    padding: '14px 14px 12px',
    gap: '12px',
    '&::-webkit-scrollbar': { display: 'none' },
  },
});
const DarkDeckCard = styled(Box, noForward(['building']))(({ building }) => ({
  flex: '0 0 300px',
  width: '300px',
  minHeight: '200px',
  background: building
    ? '#F5F5F7'
    : '#F5F5F7',
  border: building
    ? '1.5px solid #1D1D1F'
    : '1px solid #EBEBED',
  borderRadius: '22px',
  padding: '20px',
  boxShadow: building
    ? '0 1px 3px rgba(0,0,0,0.06)'
    : '0 1px 3px rgba(0,0,0,0.06)',
  scrollSnapAlign: 'start',
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'auto',
  color: '#1D1D1F',
  transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
  '&:hover': { transform: 'translateY(-4px)' },
  '@media (max-width: 600px)': {
    flex: '0 0 auto',
    width: '100%',
    minHeight: 0,
    overflowY: 'visible',
    '&:hover': { transform: 'none' },
  },
}));
const DarkDeckCardHead = styled(Box, noForward(['building']))(({ building }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  marginBottom: '18px',
  '& .num': {
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '1.4px',
    textTransform: 'uppercase',
    color: '#86868B',
  },
  '& .tag': {
    marginLeft: '6px',
    fontWeight: 500,
    letterSpacing: '0.4px',
    textTransform: 'none',
    color: '#86868B',
  },
}));
const DarkDeckBalance = styled(Box, noForward(['tone', 'building']))(
  ({ tone, building }) => {
    let color;
    let bg;
    color =
      tone === 'fav'
        ? '#34C759'
        : tone === 'unfav'
        ? '#FF3B30'
        : '#1D1D1F';
    bg = '#FFFFFF';
    return {
      fontSize: '12px',
      fontWeight: 600,
      letterSpacing: '-0.01em',
      color,
      background: bg,
      padding: '4px 10px',
      borderRadius: '980px',
      whiteSpace: 'nowrap',
    };
  }
);
const DarkDeckSide = styled(Box)({
  marginBottom: '12px',
});
const DarkDeckSideLbl = styled(Box, noForward(['building']))(({ building }) => ({
  fontSize: '10px',
  fontWeight: 700,
  letterSpacing: '1.4px',
  textTransform: 'uppercase',
  color: '#86868B',
  marginBottom: '8px',
}));
const DarkDeckArrow = styled(Box, noForward(['building']))(({ building }) => ({
  textAlign: 'center',
  fontSize: '13px',
  margin: '4px 0 8px',
  color: '#D2D2D7',
}));
const DarkDeckPills = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '6px',
});
const DarkDeckPill = styled(Box, noForward(['building']))(({ building }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  padding: '6px 10px',
  background: '#FFFFFF',
  color: '#1D1D1F',
  borderRadius: '10px',
  fontSize: '12px',
  fontWeight: 500,
  '& .face': {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    background: '#F5F5F7',
    color: '#86868B',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '9px',
    fontWeight: 700,
    flex: '0 0 20px',
    overflow: 'hidden',
  },
  '& .face img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'top center',
  },
}));
const DarkDeckCardFoot = styled(Box, noForward(['building']))(({ building }) => ({
  marginTop: 'auto',
  paddingTop: '14px',
  borderTop: '1px solid #EBEBED',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '8px',
}));
const DarkDeckCardFootInfo = styled(Box, noForward(['building']))(
  ({ building }) => ({
    fontSize: '11px',
    color: '#86868B',
    letterSpacing: '-0.01em',
  })
);
const DarkDeckCardActions = styled(Box)({
  display: 'inline-flex',
  gap: '6px',
});
const DarkDeckCardBtn = styled('button', noForward(['danger', 'building']))(
  ({ danger, building }) => ({
    height: '26px',
    padding: '0 10px',
    borderRadius: '980px',
    border: 0,
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '11px',
    fontWeight: 500,
    letterSpacing: '-0.01em',
    background: danger
      ? 'rgba(255,59,48,0.10)'
      : building
      ? '#EBEBED'
      : '#FFFFFF',
    color: danger ? '#FF3B30' : '#1D1D1F',
    transition: 'background 0.2s ease',
    '&:hover': {
      background: danger
        ? 'rgba(255,59,48,0.18)'
        : '#EBEBED',
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  })
);
const DarkDeckFoot = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  padding: '16px 24px',
  borderTop: '1px solid #EBEBED',
  background: '#FFFFFF', // white so the gray pill buttons stay visible
  '@media (max-width: 600px)': {
    flexDirection: 'column-reverse',
    alignItems: 'stretch',
    gap: '10px',
    padding: '12px 14px calc(env(safe-area-inset-bottom, 0px) + 12px)',
  },
});
const DarkDeckFootHint = styled(Box)({
  fontSize: '12px',
  color: '#6E6E73',
  letterSpacing: '-0.01em',
  '@media (max-width: 600px)': {
    textAlign: 'center',
    fontSize: '11px',
  },
});
const DarkDeckGhostCard = styled(Box)({
  flex: '0 0 300px',
  width: '300px',
  minHeight: '200px',
  background: 'transparent',
  border: '1.5px dashed #D2D2D7',
  borderRadius: '22px',
  padding: '24px',
  scrollSnapAlign: 'start',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
  cursor: 'pointer',
  color: '#86868B',
  transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
  '&:hover': {
    borderColor: '#86868B',
    color: '#1D1D1F',
    background: '#FAFAFA',
  },
  '@media (max-width: 600px)': {
    flex: '0 0 auto',
    width: '100%',
    minHeight: 0,
    padding: '18px',
  },
});
const DarkDeckGhostIcon = styled(Box)({
  fontSize: '36px',
  fontWeight: 300,
  marginBottom: '6px',
  color: 'inherit',
});
const DarkDeckGhostHead = styled(Box)({
  fontSize: '15px',
  fontWeight: 600,
  letterSpacing: '-0.01em',
  color: 'inherit',
});
const DarkDeckGhostBody = styled(Box)({
  fontSize: '13px',
  color: '#6E6E73',
  lineHeight: 1.5,
  letterSpacing: '-0.005em',
  maxWidth: '240px',
});
const DarkBtn = styled('button', noForward(['variant']))(({ variant }) => ({
  height: '40px',
  padding: '0 22px',
  borderRadius: '980px',
  border: 0,
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: '13px',
  fontWeight: 500,
  letterSpacing: '-0.01em',
  transition: 'all 0.2s ease',
  background: variant === 'primary' ? '#1D1D1F' : '#F5F5F7',
  color: variant === 'primary' ? '#FFFFFF' : '#1D1D1F',
  '&:hover': {
    transform: variant === 'primary' ? 'translateY(-1px)' : 'none',
    background: variant === 'primary' ? '#1D1D1F' : '#EBEBED',
  },
  '&:disabled': {
    background: '#F5F5F7',
    color: '#86868B',
    cursor: 'not-allowed',
    transform: 'none',
  },
  '@media (max-width: 800px)': {
    width: '100%',
    height: '46px',
    padding: '0 12px',
    fontSize: '13px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
}));

/* Mobile-only roster tab strip — only visible < 800px. Lets the user flip
 * between their roster and the partner's roster on a single column. */
const MobileRosterTabs = styled(Box)({
  display: 'none',
  '@media (max-width: 800px)': {
    order: 2,
    display: 'flex',
    gap: '6px',
    padding: '10px 12px',
    background: 'rgba(255,255,255,0.95)',
    backdropFilter: 'blur(16px)',
    borderBottom: '1px solid #EBEBED',
    position: 'sticky',
    top: '52px',
    zIndex: 25,
  },
});
const MobileRosterTab = styled('button', noForward(['active']))(({ active }) => ({
  flex: 1,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  height: '40px',
  padding: '0 10px',
  borderRadius: '12px',
  border: 0,
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: '13px',
  fontWeight: 600,
  letterSpacing: '-0.01em',
  background: active ? '#EBEBED' : 'transparent',
  color: active ? '#1D1D1F' : '#86868B',
  transition: 'background 0.2s ease',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minWidth: 0,
  '& .ct': {
    fontSize: '11px',
    fontWeight: 500,
    color: active ? '#6E6E73' : '#86868B',
  },
}));

/* ─── Component ──────────────────────────────────────────────────── */

const ALL_STEPS = [{ id: 'build', label: 'Build' }];

const ScoutCanvas = () => {
  const navigate = useNavigate();
  const { league_id, recipient_id, username } = useParams();
  const location = useLocation();
  const targetPlayer = location.state?.targetPlayer;
  const seedYourPlayer = location.state?.seedYourPlayer;
  const { user } = useAuth();
  const { goBack: goBackInHistory, canGoBack } = useNavigation();

  // Stepper: Build → Send. Scout context is folded into the Build step.
  const [step, setStep] = useState('build');
  const [scoutExpanded, setScoutExpanded] = useState(true);
  const [deckOpen, setDeckOpen] = useState(false);
  // Modal sub-phases: review cards → (anon) collect email → share URL.
  const [deckPhase, setDeckPhase] = useState('review');
  const [createdPackageLink, setCreatedPackageLink] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [packageMessage, setPackageMessage] = useState('');
  const PACKAGE_MESSAGE_MAX = 500;

  // Data state
  const [yourRoster, setYourRoster] = useState([]);
  const [theirRoster, setTheirRoster] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userRosterId, setUserRosterId] = useState(null);
  const [userSleeperUsername, setUserSleeperUsername] = useState(null);
  const [userOwner, setUserOwner] = useState(null);
  const [opponentOwner, setOpponentOwner] = useState(null);
  const [isSuperFlex, setIsSuperFlex] = useState(false);
  const [hasTEPremium, setHasTEPremium] = useState(false);
  const [tePremium, setTePremium] = useState(0);
  const [leagueName, setLeagueName] = useState(null);

  // Keep the shell's league chip honest while building in this league.
  // The discovery payload has no league name — the flow passes ?name=.
  const { setContextLeague } = useShell();
  useEffect(() => {
    const nameFromUrl = new URLSearchParams(window.location.search).get('name');
    const name = leagueName || nameFromUrl;
    if (!name) return undefined;
    setContextLeague({
      leagueId: league_id,
      name,
      numTeams: 0,
      isSuperFlex,
      ppr: 0,
      tePremium: tePremium || 0,
    });
    return () => setContextLeague(null);
  }, [leagueName, league_id, isSuperFlex, tePremium, setContextLeague]);

  const [intelStats, setIntelStats] = useState(null);
  const [intelProfile, setIntelProfile] = useState(null);
  const [partnerTrades, setPartnerTrades] = useState([]);
  const [intelSyncing, setIntelSyncing] = useState(false);
  const [intelToastName, setIntelToastName] = useState(null);
  const intelPollRef = useRef(null);

  // Dedupe rolled-over leagues — a 2026 league with previousLeagueId pointing to a 2025
  // league is the SAME league. Show only the newest version, but remember the historical
  // chain so the "you're here" badge still matches when the URL points at an older season.
  const mergedLeagues = useMemo(() => {
    const leagues = intelProfile?.leagues || [];
    if (leagues.length === 0) return [];

    const hasNextVersion = new Set();
    leagues.forEach((lg) => {
      if (lg.settings?.previousLeagueId) {
        hasNextVersion.add(lg.settings.previousLeagueId);
      }
    });

    const sortedLeagues = [...leagues].sort(
      (a, b) => parseInt(b.season, 10) - parseInt(a.season, 10)
    );

    const merged = [];
    const processed = new Set();

    sortedLeagues.forEach((league) => {
      if (processed.has(league.sleeperLeagueId)) return;
      if (hasNextVersion.has(league.sleeperLeagueId)) {
        processed.add(league.sleeperLeagueId);
        return;
      }

      const chainSleeperIds = [league.sleeperLeagueId];
      let cursor = league;
      while (cursor.settings?.previousLeagueId) {
        const prev = leagues.find(
          (l) => l.sleeperLeagueId === cursor.settings.previousLeagueId
        );
        if (!prev) break;
        chainSleeperIds.push(prev.sleeperLeagueId);
        processed.add(prev.sleeperLeagueId);
        cursor = prev;
      }
      processed.add(league.sleeperLeagueId);

      merged.push({ ...league, chainSleeperIds });
    });

    return merged;
  }, [intelProfile?.leagues]);

  // Trade build state
  const [yourSide, setYourSide] = useState(seedYourPlayer ? [seedYourPlayer] : []);
  const [theirSide, setTheirSide] = useState(targetPlayer ? [targetPlayer] : []);
  const [packageTrades, setPackageTrades] = useState([]);
  const [activeTradeIdx, setActiveTradeIdx] = useState(null); // null = building new

  const [yourPosFilter, setYourPosFilter] = useState('ALL');
  const [theirPosFilter, setTheirPosFilter] = useState('ALL');
  // Mobile-only — which roster pane is currently visible. Defaults to the
  // user's own roster since most flows start by picking what to give.
  const [mobileRosterTab, setMobileRosterTab] = useState('left');

  // Send state
  const [saving, setSaving] = useState(false);
  const [shouldAutoSubmit, setShouldAutoSubmit] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  const [magicLinkSentTo, setMagicLinkSentTo] = useState(null);
  const [sendError, setSendError] = useState(null);

  // Tweets
  const [includeTweets, setIncludeTweets] = useState(true);
  // Per-trade map of available tweet IDs that the carousel has fetched.
  const [tweetIdsByTrade, setTweetIdsByTrade] = useState({});

  // 2H BUILD VIEW state — intel tabs/filters, tweets matched to current trade,
  // and player-name lookup for trade comp cards.
  const [intelTab, setIntelTab] = useState('trades');
  const [tradesFilter, setTradesFilter] = useState('this-trade');
  const [buildTweets, setBuildTweets] = useState([]);
  const [tradePlayerMap, setTradePlayerMap] = useState({});
  // Cross-user comp trades (Anyone · this trade) — fetched lazily.
  const [comps, setComps] = useState([]);
  const [compsLoading, setCompsLoading] = useState(false);

  /* ESC closes the deck modal */
  useEffect(() => {
    if (!deckOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setDeckOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [deckOpen]);

  /* Restore on auth-return */
  useEffect(() => {
    const returnState = location.state?.returnState;
    if (returnState?.packageTrades) setPackageTrades(returnState.packageTrades);
    if (location.state?.restorePackage && location.state?.packageTrades) {
      setPackageTrades(location.state.packageTrades);
      setShouldAutoSubmit(true);
      setStep('build');
    }
  }, [location.state]);

  /* Fetch rosters */
  useEffect(() => {
    let cancelled = false;
    async function fetchRosters() {
      try {
        setLoading(true);
        const discoveryUrl = username
          ? `${API_URL}/discovery/${username}/leagues/${league_id}`
          : `${API_URL}/discovery/leagues/${league_id}`;
        const discoveryRes = await fetch(discoveryUrl);
        const discoveryData = await discoveryRes.json();
        if (cancelled) return;
        if (discoveryData.error) {
          setError(discoveryData.error);
          return;
        }
        const userRosterIdFromApi = discoveryData.userRoster.roster_id;
        setUserRosterId(userRosterIdFromApi);

        const url = `${API_URL}/discovery/leagues/${league_id}/rosters/${userRosterIdFromApi}/opponent/${recipient_id}`;
        const res = await fetch(url);
        const data = await res.json();
        if (cancelled) return;
        if (data.error) {
          setError(data.error);
        } else {
          setYourRoster(data.userRoster.players || []);
          setTheirRoster(data.opponentRoster.players || []);
          if (data.userRoster.owner?.username) setUserSleeperUsername(data.userRoster.owner.username);
          if (data.userRoster.owner) setUserOwner(data.userRoster.owner);
          if (data.opponentRoster.owner) setOpponentOwner(data.opponentRoster.owner);
          if (data.league?.isSuperFlex !== undefined) setIsSuperFlex(data.league.isSuperFlex);
          if (data.league?.hasTEPremium !== undefined) setHasTEPremium(data.league.hasTEPremium);
          if (data.league?.tePremium !== undefined) setTePremium(Number(data.league.tePremium) || 0);
          if (data.league?.name) setLeagueName(data.league.name);
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchRosters();
    return () => {
      cancelled = true;
    };
  }, [league_id, recipient_id, username]);

  /* Manager Intel — load profile, auto-sync if needed, poll for completion. */
  useEffect(() => {
    if (!opponentOwner?.username) return;
    const partnerUsername = opponentOwner.username;
    const viewer = userSleeperUsername || username;
    let cancelled = false;

    const computeStats = (trades) => {
      const list = trades || [];
      const now = Date.now();
      const ninetyDaysAgo = now - 90 * 24 * 60 * 60 * 1000;
      const recent = list.filter((t) => new Date(t.tradedAt).getTime() >= ninetyDaysAgo);
      const sorted = [...list].sort(
        (a, b) => new Date(b.tradedAt).getTime() - new Date(a.tradedAt).getTime()
      );
      const lastTrade = sorted.length > 0 ? new Date(sorted[0].tradedAt) : null;
      const sinceLast = lastTrade
        ? Math.max(0, Math.round((now - lastTrade.getTime()) / (24 * 60 * 60 * 1000)))
        : null;
      return {
        tradesIn90d: recent.length,
        sinceLastTradeDays: sinceLast,
        totalTrades: list.length,
      };
    };

    const loadProfile = async () => {
      // Pull both endpoints. Profile gives trophy case + leagues; partner-trades
      // is the visible trade list — using it for the stats keeps the numbers in
      // sync with what the user sees below.
      const [profileRes, partnerRes] = await Promise.allSettled([
        managerIntelAPI.getProfile(partnerUsername, viewer),
        managerIntelAPI.getPartnerTrades(partnerUsername, viewer),
      ]);
      if (cancelled) return null;
      const profile = profileRes.status === 'fulfilled' ? profileRes.value.data || {} : {};
      setIntelProfile(profile);
      const visibleTrades =
        partnerRes.status === 'fulfilled' ? partnerRes.value.data?.trades || [] : [];
      setIntelStats(computeStats(visibleTrades));
      setPartnerTrades(visibleTrades);
      return profile;
    };

    const stopPolling = () => {
      if (intelPollRef.current) {
        clearInterval(intelPollRef.current);
        intelPollRef.current = null;
      }
    };

    const startPolling = () => {
      stopPolling();
      intelPollRef.current = setInterval(async () => {
        try {
          const statusRes = await managerIntelAPI.getSyncStatus(partnerUsername);
          if (cancelled) return;
          const status = statusRes.data?.status;
          if (status === 'complete' || status === 'error') {
            stopPolling();
            setIntelSyncing(false);
            if (status === 'complete') {
              await loadProfile();
              if (cancelled) return;
              setIntelToastName(opponentOwner.display_name || partnerUsername);
              setTimeout(() => {
                if (!cancelled) setIntelToastName(null);
              }, 4000);
            }
          }
        } catch (_err) {
          // Swallow polling errors; safety timeout will stop polling.
        }
      }, 2000);

      // Safety timeout — stop polling after 2 minutes even if status never resolves.
      setTimeout(() => {
        if (intelPollRef.current) {
          stopPolling();
          if (!cancelled) setIntelSyncing(false);
        }
      }, 120000);
    };

    (async () => {
      try {
        await loadProfile();
        if (cancelled) return;

        const statusRes = await managerIntelAPI.getSyncStatus(partnerUsername);
        if (cancelled) return;
        const status = statusRes.data?.status;

        if (status === 'in_progress') {
          setIntelSyncing(true);
          startPolling();
          return;
        }

        if (status === 'not_started' || !statusRes.data?.lastRefreshedAt) {
          setIntelSyncing(true);
          try {
            await managerIntelAPI.triggerSync(partnerUsername, league_id);
          } catch (_err) {
            // If trigger fails (e.g., rate limit), still poll — sync may already be queued.
          }
          if (cancelled) return;
          startPolling();
        }
      } catch (_err) {
        // Silent fall-through if anything fails — UI just won't show intel.
      }
    })();

    return () => {
      cancelled = true;
      if (intelPollRef.current) {
        clearInterval(intelPollRef.current);
        intelPollRef.current = null;
      }
    };
  }, [opponentOwner?.username, userSleeperUsername, username, league_id]);

  /* Auto-submit after auth-return */
  useEffect(() => {
    if (
      shouldAutoSubmit &&
      !loading &&
      user?.email &&
      userRosterId &&
      userSleeperUsername &&
      opponentOwner &&
      packageTrades.length > 0
    ) {
      setShouldAutoSubmit(false);
      handleSendPackage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldAutoSubmit, loading, user, userRosterId, userSleeperUsername, opponentOwner, packageTrades]);

  /* 2H BUILD VIEW — fetch tweets matched against the current build's players */
  const buildPlayerKey = useMemo(
    () =>
      [...yourSide, ...theirSide]
        .filter((p) => p.position !== 'PICK')
        .map((p) => String(p.id))
        .sort()
        .join(','),
    [yourSide, theirSide]
  );

  useEffect(() => {
    const ids = buildPlayerKey ? buildPlayerKey.split(',') : [];
    if (ids.length === 0) {
      setBuildTweets([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await tweetsAPI.forPlayers(ids, 50);
        if (!cancelled) setBuildTweets(res.data?.tweets || []);
      } catch (_err) {
        if (!cancelled) setBuildTweets([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [buildPlayerKey]);

  /* 2H BUILD VIEW — batch-load player names for partner-trade + comp pills. */
  useEffect(() => {
    const ids = new Set();
    (partnerTrades || []).forEach((t) => {
      Object.keys(t.partnerAdds || {}).forEach((pid) => ids.add(pid));
      Object.keys(t.partnerDrops || {}).forEach((pid) => ids.add(pid));
    });
    (comps || []).forEach((t) => {
      (t.sides || []).forEach((s) => {
        Object.keys(s.adds || {}).forEach((pid) => ids.add(pid));
        Object.keys(s.drops || {}).forEach((pid) => ids.add(pid));
      });
    });
    const missing = Array.from(ids).filter((id) => !tradePlayerMap[id]);
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await playersAPI.getBatch(missing);
        if (!cancelled) {
          setTradePlayerMap((prev) => ({ ...prev, ...(res.data?.players || {}) }));
        }
      } catch (_err) {
        // Silent fail — trade pills will show player IDs as fallback.
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerTrades, comps]);

  /* 2H BUILD VIEW — fetch cross-user trade comps for the active build. */
  const compsKey = useMemo(() => {
    const players = [...yourSide, ...theirSide]
      .filter((p) => p && p.position !== 'PICK')
      .map((p) => String(p.id))
      .sort();
    const picks = [...yourSide, ...theirSide]
      .filter((p) => p && p.position === 'PICK' && p.season && p.round)
      .map((p) => `${p.season}:${p.round}`)
      .sort();
    return `${players.join(',')}|${picks.join(',')}`;
  }, [yourSide, theirSide]);

  // Debounce so rapid player/pick toggles don't fire 5+ requests in flight.
  useEffect(() => {
    if (!compsKey || compsKey === '|') {
      setComps([]);
      return;
    }
    const [playersStr, picksStr] = compsKey.split('|');
    const players = playersStr ? playersStr.split(',') : [];
    const picks = picksStr ? picksStr.split(',') : [];
    if (players.length === 0 && picks.length === 0) {
      setComps([]);
      return;
    }
    let cancelled = false;
    setCompsLoading(true);
    const timer = setTimeout(() => {
      (async () => {
        try {
          const res = await managerIntelAPI.getTradeComps(players, picks, 50);
          if (!cancelled) setComps(res.data?.trades || []);
        } catch (_err) {
          if (!cancelled) setComps([]);
        } finally {
          if (!cancelled) setCompsLoading(false);
        }
      })();
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [compsKey]);

  /* Toggle players */
  const togglePlayerYourSide = (player) => {
    setYourSide((prev) =>
      prev.find((p) => p.id === player.id) ? prev.filter((p) => p.id !== player.id) : [...prev, player]
    );
  };
  const togglePlayerTheirSide = (player) => {
    setTheirSide((prev) =>
      prev.find((p) => p.id === player.id) ? prev.filter((p) => p.id !== player.id) : [...prev, player]
    );
  };

  /* Lock current trade into the package */
  const addToPackage = () => {
    if (yourSide.length === 0 || theirSide.length === 0) return;
    const balance = computeBalance(yourSide, theirSide);
    // Snapshot the buzz currently matched against this trade so it travels with
    // the package even though we no longer have a separate review step.
    const tweetIds = (buildTweets || [])
      .map((t) => t?.id)
      .filter(Boolean);
    const newTrade = {
      id: Date.now(),
      yourSide: [...yourSide],
      theirSide: [...theirSide],
      balance: { label: balance.label, color: '#1D1D1F', tone: balance.tone, sub: balance.sub },
      tweetIds,
      timestamp: new Date(),
    };
    setPackageTrades((prev) => [...prev, newTrade]);
    if (tweetIds.length > 0) {
      setTweetIdsByTrade((prev) => ({ ...prev, [newTrade.id]: tweetIds }));
    }
    setYourSide([]);
    setTheirSide([]);
    setActiveTradeIdx(null);
  };

  const removeFromPackage = (tradeId) => {
    setPackageTrades((prev) => prev.filter((t) => t.id !== tradeId));
  };

  /* Click a queued trade tab → load its players back into the active draft.
   * If the current draft has content, it gets parked into that slot (swap). */
  const loadTradeForEditing = (idx) => {
    const clicked = packageTrades[idx];
    if (!clicked) return;
    const draftHasContent = yourSide.length > 0 || theirSide.length > 0;

    if (draftHasContent) {
      const draftBalance = computeBalance(yourSide, theirSide);
      const draftAsTrade = {
        id: Date.now(),
        yourSide: [...yourSide],
        theirSide: [...theirSide],
        balance: {
          label: draftBalance.label,
          color: '#1D1D1F',
          tone: draftBalance.tone,
          sub: draftBalance.sub,
        },
        timestamp: new Date(),
      };
      setPackageTrades((prev) => prev.map((t, i) => (i === idx ? draftAsTrade : t)));
    } else {
      setPackageTrades((prev) => prev.filter((_, i) => i !== idx));
    }

    setYourSide(clicked.yourSide);
    setTheirSide(clicked.theirSide);
    setActiveTradeIdx(null);
  };

  /* Send */
  const handleSendPackage = async () => {
    const tradesToSend = (() => {
      if (yourSide.length > 0 && theirSide.length > 0) {
        const balance = computeBalance(yourSide, theirSide);
        return [
          ...packageTrades,
          {
            id: Date.now(),
            yourSide: [...yourSide],
            theirSide: [...theirSide],
            balance: { label: balance.label, color: '#1D1D1F', tone: balance.tone, sub: balance.sub },
            timestamp: new Date(),
          },
        ];
      }
      return packageTrades;
    })();
    if (tradesToSend.length === 0) return;

    const emailToUse = (user?.email || pendingEmail || '').trim();
    if (!emailToUse) {
      setSendError('Add your email so we can let you know when they respond.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailToUse)) {
      setSendError('That email doesn’t look right.');
      return;
    }

    setSaving(true);
    setSendError(null);
    try {
      const formattedTrades = tradesToSend.map((trade) => ({
        creatorItems: trade.yourSide.map((player) =>
          player.position === 'PICK'
            ? { type: 'pick', pickData: player.pickData || {}, name: player.name }
            : {
                type: 'player',
                playerId: player.id,
                name: player.name,
                position: player.position,
                team: player.team,
              }
        ),
        recipientItems: trade.theirSide.map((player) =>
          player.position === 'PICK'
            ? { type: 'pick', pickData: player.pickData || {}, name: player.name }
            : {
                type: 'player',
                playerId: player.id,
                name: player.name,
                position: player.position,
                team: player.team,
              }
        ),
      }));

      // Tweets attached to locked trades (snapshotted at lock-in) plus any buzz
      // matched against the in-progress trade we just baked in.
      const tweetIdSet = new Set(Object.values(tweetIdsByTrade).flat());
      if (yourSide.length > 0 && theirSide.length > 0) {
        (buildTweets || []).forEach((t) => {
          if (t?.id) tweetIdSet.add(t.id);
        });
      }

      // Always send the actual Sleeper user_id (immutable) — never the
      // roster_id. The URL :recipient_id param happens to be the roster
      // id; the real user_id lives on opponentOwner.user_id from the
      // discovery API response.
      const payload = {
        email: emailToUse,
        leagueId: league_id,
        recipientSleeperUserId: opponentOwner?.user_id || null,
        recipientDisplayName: opponentOwner?.display_name || 'Team Owner',
        creatorSleeperUserId: userOwner?.user_id || null,
        creatorSleeperUsername: userSleeperUsername,
        // Stick to Sleeper identity for the public-facing display name —
        // the recipient sees this and we don't want to leak email addresses.
        creatorDisplayName:
          userOwner?.display_name ||
          userOwner?.username ||
          userSleeperUsername ||
          user?.displayName ||
          'Trade partner',
        trades: formattedTrades,
        tweetIds: includeTweets ? Array.from(tweetIdSet) : [],
        message: packageMessage.trim() || undefined,
      };

      const response = await tradePackagesAPI.create(payload);
      const uniqueLink = response.data?.uniqueLink;
      if (uniqueLink) {
        const fullUrl = `${window.location.origin}/trade-packages/${uniqueLink}`;
        setCreatedPackageLink(fullUrl);
        // Move the modal into share mode so the user can copy + paste into Sleeper.
        setDeckOpen(true);
        setDeckPhase('share');
      } else if (response.data?.pendingVerification) {
        // Email isn't verified — magic link was sent, package is held until they verify.
        setMagicLinkSentTo(response.data.email || emailToUse);
        setDeckOpen(true);
        setDeckPhase('verify');
      }
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to send trade package';
      setSendError(msg);
    } finally {
      setSaving(false);
    }
  };

  /* Tweets — record what the carousel fetched for each trade */
  const setTradeTweetIds = (tradeId, ids) => {
    setTweetIdsByTrade((prev) => {
      const next = { ...prev };
      if (ids.length === 0) {
        delete next[tradeId];
      } else {
        next[tradeId] = ids;
      }
      return next;
    });
  };

  const totalTweetsAttached = useMemo(
    () =>
      includeTweets
        ? new Set(Object.values(tweetIdsByTrade).flat()).size
        : 0,
    [includeTweets, tweetIdsByTrade]
  );

  /* All hooks must run on every render — placed before any early returns. */

  /* Step navigation — signin step is hidden when the user already has an email. */
  const STEPS = useMemo(
    () => ALL_STEPS.filter((s) => s.id !== 'signin' || !user?.email),
    [user?.email]
  );

  // If the user signs in mid-flow while sitting on the signin step, drop them
  // back to the build view — handleSendPackage will fire from there.
  useEffect(() => {
    if (step === 'signin' && user?.email) setStep('build');
  }, [step, user?.email]);

  /* 2H BUILD VIEW — derived sets / filters (must be hooks; placed before early returns). */
  // Player IDs from non-pick selections in the active build.
  const currentTradeIdSet = useMemo(() => {
    const s = new Set();
    [...yourSide, ...theirSide].forEach((p) => {
      if (p && p.position !== 'PICK') s.add(String(p.id));
    });
    return s;
  }, [yourSide, theirSide]);

  // Pick keys (season:round) for picks in the active build. Generic by design —
  // a 2026 1st in the trade matches any 2026 1st in the comp set.
  const currentTradePickKeySet = useMemo(() => {
    const s = new Set();
    [...yourSide, ...theirSide].forEach((p) => {
      if (p && p.position === 'PICK' && p.season && p.round) {
        s.add(`${p.season}:${p.round}`);
      }
    });
    return s;
  }, [yourSide, theirSide]);

  const myRosterIdSet = useMemo(() => {
    const s = new Set();
    yourRoster.forEach((p) => {
      if (p && p.position !== 'PICK') s.add(String(p.id));
    });
    return s;
  }, [yourRoster]);

  // Match a trade against player IDs and/or pick keys.
  // For partner trades, picks live on draftPicksTraded (season + round).
  const tradeHits = (trade, playerIdSet, pickKeySet) => {
    const hasPlayers = playerIdSet && playerIdSet.size > 0;
    const hasPicks = pickKeySet && pickKeySet.size > 0;
    if (!hasPlayers && !hasPicks) return false;
    if (hasPlayers) {
      const ids = [
        ...Object.keys(trade.partnerAdds || {}),
        ...Object.keys(trade.partnerDrops || {}),
      ];
      if (ids.some((id) => playerIdSet.has(String(id)))) return true;
    }
    if (hasPicks) {
      const picks = trade.draftPicksTraded || [];
      if (picks.some((p) => pickKeySet.has(`${p.season}:${p.round}`))) return true;
    }
    return false;
  };

  const intelTradeCounts = useMemo(() => {
    const all = partnerTrades || [];
    return {
      'all-theirs': all.length,
      'my-players': all.filter((t) => tradeHits(t, myRosterIdSet, null)).length,
      'this-trade': all.filter((t) =>
        tradeHits(t, currentTradeIdSet, currentTradePickKeySet)
      ).length,
      'any-user': (comps || []).length,
    };
    // tradeHits is stable; eslint-disable next line
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerTrades, myRosterIdSet, currentTradeIdSet, currentTradePickKeySet, comps]);

  // Pick the most useful default filter once trade data arrives. Prefer
  // "this deal" hits; fall back through "your players" → "all theirs" → "any-user".
  // Only fires until the user manually picks a filter.
  const tradesFilterTouched = useRef(false);
  useEffect(() => {
    if (tradesFilterTouched.current) return;
    if (!partnerTrades || partnerTrades.length === 0) return;
    if (intelTradeCounts['this-trade'] > 0) {
      setTradesFilter('this-trade');
    } else if (intelTradeCounts['my-players'] > 0) {
      setTradesFilter('my-players');
    } else if (intelTradeCounts['all-theirs'] > 0) {
      setTradesFilter('all-theirs');
    } else {
      setTradesFilter('any-user');
    }
  }, [partnerTrades, intelTradeCounts]);

  const handleTradesFilterChange = (next) => {
    tradesFilterTouched.current = true;
    setTradesFilter(next);
  };

  const filteredPartnerTrades = useMemo(() => {
    const all = partnerTrades || [];
    switch (tradesFilter) {
      case 'all-theirs':
        return all;
      case 'my-players':
        return all.filter((t) => tradeHits(t, myRosterIdSet, null));
      case 'this-trade':
        return all.filter((t) => tradeHits(t, currentTradeIdSet, currentTradePickKeySet));
      case 'any-user':
        return [];
      default:
        return all;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerTrades, tradesFilter, myRosterIdSet, currentTradeIdSet, currentTradePickKeySet]);

  const intelMatchedNames = useMemo(() => {
    const names = [...yourSide, ...theirSide].map((p) => p.name).filter(Boolean);
    if (names.length === 0) return null;
    if (names.length === 1) return <><b>{names[0]}</b></>;
    if (names.length === 2)
      return (
        <>
          <b>{names[0]}</b> and <b>{names[1]}</b>
        </>
      );
    return (
      <>
        {names.slice(0, -1).map((n, i) => (
          <React.Fragment key={n}>
            <b>{n}</b>
            {i < names.length - 2 ? ', ' : ''}
          </React.Fragment>
        ))}
        , and <b>{names[names.length - 1]}</b>
      </>
    );
  }, [yourSide, theirSide]);

  /* Loading / error */
  if (loading) {
    return (
      <PageContainer>
        <TradePackagesHeader />
        <Loading>
          <FTLoading label="Assembling the trade canvas…" style={{ padding: 0 }} />
        </Loading>
      </PageContainer>
    );
  }
  if (error) {
    return (
      <PageContainer>
        <TradePackagesHeader />
        <ErrorState>{error}</ErrorState>
      </PageContainer>
    );
  }

  const partnerName = opponentOwner?.display_name || opponentOwner?.username || 'Partner';
  const partnerHandle = opponentOwner?.username || '';
  const balance = computeBalance(yourSide, theirSide);
  const ktcLink = generateKTCLink(yourSide, theirSide, isSuperFlex, tePremium, hasTEPremium);
  const totalTradesInPackage =
    packageTrades.length + (yourSide.length > 0 || theirSide.length > 0 ? 1 : 0);

  const stepIndex = STEPS.findIndex((s) => s.id === step);

  const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((e || '').trim());

  // Single entry point from the build view's "Send deck →" button — opens the
  // modal so the user reviews the cards, collects email if anon, then shares.
  const goNext = () => {
    setDeckPhase('review');
    setDeckOpen(true);
  };
  const goBack = () => {};

  // Advance from the modal's review phase: logged-in → submit; anon → email step.
  const advanceFromReview = () => {
    if (user?.email) {
      handleSendPackage(); // success handler advances to 'share'
    } else {
      setDeckPhase('email');
    }
  };

  const copyLinkToClipboard = async () => {
    if (!createdPackageLink) return;
    try {
      await navigator.clipboard.writeText(createdPackageLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (_err) {
      // Older browsers — quick fallback.
      const ta = document.createElement('textarea');
      ta.value = createdPackageLink;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); setLinkCopied(true); setTimeout(() => setLinkCopied(false), 2000); } catch (_) {}
      document.body.removeChild(ta);
    }
  };

  const canAdvanceFromBuild = totalTradesInPackage > 0;
  const canAdvanceFromSignin = isValidEmail(pendingEmail);
  const canSend = packageTrades.length > 0 || (yourSide.length > 0 && theirSide.length > 0);

  /* Tweet scope per trade for the send step */
  const filterPlayers = (arr) =>
    (arr || []).filter((p) => p && p.id && p.position !== 'PICK');

  /* 2H BUILD VIEW computed values */
  const yourName =
    userOwner?.display_name || userOwner?.username || userSleeperUsername || 'You';
  const yourHandle =
    userOwner?.username || userSleeperUsername || (user?.email || '').split('@')[0] || 'you';
  const yourReceives = theirSide; // what user gets ends up on user's side
  const theirReceives = yourSide; // what user gives ends up on partner's side
  const buildingNum = packageTrades.length + 1;

  const posCounts = (roster) => {
    const c = { ALL: roster.length, QB: 0, RB: 0, WR: 0, TE: 0, PICK: 0 };
    roster.forEach((p) => {
      if (c[p.position] !== undefined) c[p.position]++;
    });
    return c;
  };
  const yourCounts = posCounts(yourRoster);
  const theirCounts = posCounts(theirRoster);
  const yourPicks = yourRoster.filter((p) => p.position === 'PICK').length;
  const theirPicks = theirRoster.filter((p) => p.position === 'PICK').length;
  const yourPlayersCount = yourRoster.length - yourPicks;
  const theirPlayersCount = theirRoster.length - theirPicks;

  const renderDealPill = (playerId, isMatch = false) => {
    const player = tradePlayerMap[playerId];
    const url = sleeperPlayerThumb(playerId);
    return (
      <DarkDealPill key={playerId} match={isMatch}>
        <span className="face">
          {url ? (
            <img
              src={url}
              alt=""
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.parentNode.textContent = (player?.position || '?').slice(0, 2);
              }}
            />
          ) : (
            (player?.position || '?').slice(0, 2)
          )}
        </span>
        {player?.name || `Player ${playerId}`}
      </DarkDealPill>
    );
  };
  const formatPickKey = (pick) => {
    const round = pick.round;
    const suffix = round === 1 ? 'st' : round === 2 ? 'nd' : round === 3 ? 'rd' : 'th';
    return `${pick.season} ${round}${suffix}`;
  };
  const renderPickPill = (pick, key) => (
    <DarkDealPill key={key}>
      <span className="face">PCK</span>
      {formatPickKey(pick)}
    </DarkDealPill>
  );
  const formatLeagueSettings = (league) => {
    const s = league?.settings || {};
    const parts = [];
    if (s.numTeams) parts.push(`${s.numTeams}-team`);
    if (s.type) parts.push(s.type === 'dynasty' ? 'Dynasty' : 'Redraft');
    if (s.isSuperFlex) parts.push('Superflex');
    if (s.tePremium) parts.push(`TEP ${s.tePremium}`);
    if (typeof s.ppr === 'number' && s.ppr > 0) {
      parts.push(`${s.ppr === 1 ? '1.0' : s.ppr === 0.5 ? '0.5' : s.ppr} PPR`);
    } else if (s.ppr === 0) {
      parts.push('Standard');
    }
    return parts.join(' · ');
  };
  const formatRelativeTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const days = Math.floor((now - d) / (24 * 60 * 60 * 1000));
    if (days < 1) return 'Today';
    if (days < 7) return `${days}d ago`;
    if (days < 30) return `${Math.floor(days / 7)}w ago`;
    if (days < 365) return `${Math.floor(days / 30)}mo ago`;
    return `${Math.floor(days / 365)}y ago`;
  };

  /* ─── BUILD-STEP DARK RENDER ─────────────────────────────────────── */
  if (step === 'build') {
    const renderRosterPane = (side) => {
      const isYou = side === 'left';
      const roster = isYou ? yourRoster : theirRoster;
      const counts = isYou ? yourCounts : theirCounts;
      const filter = isYou ? yourPosFilter : theirPosFilter;
      const setFilter = isYou ? setYourPosFilter : setTheirPosFilter;
      const onClickPlayer = isYou ? togglePlayerYourSide : togglePlayerTheirSide;
      const selected = isYou ? yourSide : theirSide;
      const owner = isYou ? userOwner : opponentOwner;
      const headerName = isYou ? 'Your roster' : owner?.display_name || owner?.username || 'Partner';
      const playersN = isYou ? yourPlayersCount : theirPlayersCount;
      const picksN = isYou ? yourPicks : theirPicks;
      const isHiddenOnMobile =
        (side === 'left' && mobileRosterTab !== 'left') ||
        (side === 'right' && mobileRosterTab !== 'right');
      return (
        <DarkRosterPane side={side} mobileHidden={isHiddenOnMobile}>
          <DarkRosterHead>
            <DarkRosterTitleRow>
              <UserAvatarImg
                avatar={owner?.avatar}
                fallback={headerName}
                size={32}
                tone={isYou ? 'you' : 'them'}
              />
              <Box>
                <DarkRosterName>{headerName}</DarkRosterName>
                {!isYou && owner?.username ? (
                  <Box
                    component="a"
                    href={`/manager-intel/${owner.username}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      textDecoration: 'none',
                      marginTop: '4px',
                      padding: '4px 10px',
                      borderRadius: '980px',
                      background: '#F5F5F7',
                      color: '#1D1D1F',
                      fontSize: '12px',
                      fontWeight: 500,
                      letterSpacing: '-0.01em',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        background: '#EBEBED',
                        color: '#1D1D1F',
                      },
                    }}
                  >
                    See manager intel ↗
                  </Box>
                ) : (
                  <DarkRosterSub>
                    {playersN} player{playersN === 1 ? '' : 's'} · {picksN} pick{picksN === 1 ? '' : 's'}
                  </DarkRosterSub>
                )}
              </Box>
            </DarkRosterTitleRow>
            <DarkPosTabs>
              {POSITION_FILTERS.map((f) => {
                const key = f === 'PICKS' ? 'PICK' : f;
                const ct = f === 'ALL' ? counts.ALL : counts[key] || 0;
                return (
                  <DarkPos key={f} active={filter === f} onClick={() => setFilter(f)}>
                    {f === 'ALL' ? 'All' : f === 'PICKS' ? 'PCK' : f}
                    <span className="ct">{ct}</span>
                  </DarkPos>
                );
              })}
            </DarkPosTabs>
          </DarkRosterHead>
          <DarkPlayerList>
            {filterRoster(roster, filter).map((player) => {
              const added = !!selected.find((p) => p.id === player.id);
              return (
                <DarkPlayer key={player.id} added={added} onClick={() => onClickPlayer(player)}>
                  {player.position === 'PICK' ? (
                    <DarkPlayerFace added={added}>PICK</DarkPlayerFace>
                  ) : (
                    <DarkPlayerAvatarImg
                      sleeperId={player.id}
                      fallback={player.position}
                      added={added}
                    />
                  )}
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <DarkPlayerName>{player.name}</DarkPlayerName>
                    <DarkPlayerMeta added={added}>
                      {player.position === 'PICK'
                        ? player.season || 'pick'
                        : `${player.position}${player.team ? ` · ${player.team}` : ''}`}
                    </DarkPlayerMeta>
                  </Box>
                </DarkPlayer>
              );
            })}
          </DarkPlayerList>
        </DarkRosterPane>
      );
    };

    const renderPillFor = (player, align) =>
      player.position === 'PICK' ? (
        <DarkPill key={player.id} align={align}>
          <DarkPillFace>PCK</DarkPillFace>
          <DarkPillName>{player.name}</DarkPillName>
          <DarkPillX
            onClick={(e) => {
              e.stopPropagation();
              if (align === 'left') togglePlayerTheirSide(player);
              else togglePlayerYourSide(player);
            }}
          >
            ×
          </DarkPillX>
        </DarkPill>
      ) : (
        <DarkPill key={player.id} align={align}>
          <DarkPillFaceImg sleeperId={player.id} fallback={player.position} />
          <DarkPillName>{player.name}</DarkPillName>
          <DarkPillX
            onClick={(e) => {
              e.stopPropagation();
              if (align === 'left') togglePlayerTheirSide(player);
              else togglePlayerYourSide(player);
            }}
          >
            ×
          </DarkPillX>
        </DarkPill>
      );

    return (
      <DarkPage>
        {/* Chrome lives in the shell topbar — the canvas keeps only its Back action */}
        <ShellTopBarActions>
          <Box
            component="button"
            onClick={() => (canGoBack() ? goBackInHistory() : navigate('/trade-packages'))}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              height: '34px',
              padding: '0 14px',
              border: 0,
              borderRadius: '980px',
              background: '#F5F5F7',
              color: '#1D1D1F',
              fontSize: '13px',
              fontWeight: 500,
              letterSpacing: '-0.01em',
              fontFamily: 'inherit',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': { background: '#EBEBED' },
            }}
          >
            <ArrowBack sx={{ fontSize: '18px' }} />
            Back
          </Box>
        </ShellTopBarActions>

        <DarkStage>
          {renderRosterPane('left')}

          <DarkCenterStage>
            {/* Trade card — two-column receive layout */}
            <DarkTradeCard>
              <DarkTradeNum>
                Trade {buildingNum} · <b>building</b>
              </DarkTradeNum>
              <DarkReceiveGrid>
                <DarkReceiveCol>
                  <DarkReceiveHead align="left">
                    <UserAvatarImg
                      avatar={userOwner?.avatar}
                      fallback={yourName}
                      size={26}
                      tone="you"
                    />
                    <DarkReceiveWho>
                      <DarkReceiveName>@{yourHandle}</DarkReceiveName>
                      <DarkReceiveLabel>Receives</DarkReceiveLabel>
                    </DarkReceiveWho>
                  </DarkReceiveHead>
                  <DarkPillStack>
                    {yourReceives.length === 0 ? (
                      <DarkPillEmpty>Pick from {yourName === 'You' ? 'partner’s' : 'their'} roster</DarkPillEmpty>
                    ) : (
                      yourReceives.map((p) => renderPillFor(p, 'left'))
                    )}
                  </DarkPillStack>
                </DarkReceiveCol>

                <DarkSwapDivider>⇄</DarkSwapDivider>

                <DarkReceiveCol>
                  <DarkReceiveHead align="right">
                    <UserAvatarImg
                      avatar={opponentOwner?.avatar}
                      fallback={opponentOwner?.username || partnerName}
                      size={26}
                    />
                    <DarkReceiveWho>
                      <DarkReceiveName>@{partnerHandle || partnerName}</DarkReceiveName>
                      <DarkReceiveLabel>Receives</DarkReceiveLabel>
                    </DarkReceiveWho>
                  </DarkReceiveHead>
                  <DarkPillStack>
                    {theirReceives.length === 0 ? (
                      <DarkPillEmpty>Pick from your roster</DarkPillEmpty>
                    ) : (
                      theirReceives.map((p) => renderPillFor(p, 'right'))
                    )}
                  </DarkPillStack>
                </DarkReceiveCol>
              </DarkReceiveGrid>

              <DarkKtcLink href={ktcLink} target="_blank" rel="noopener noreferrer">
                Open on KeepTradeCut ↗
              </DarkKtcLink>
            </DarkTradeCard>

            {/* Trade Intel section */}
            <DarkIntel>
              <DarkIntelHead>
                <Box>
                  <DarkIntelTitle>Trade intel</DarkIntelTitle>
                  <DarkIntelSub>
                    {intelMatchedNames ? (
                      <>
                        Tweets and trades involving {intelMatchedNames} — switch tabs to slice
                        the data.
                      </>
                    ) : (
                      <>Pick players for this trade to see related tweets and comp trades.</>
                    )}
                  </DarkIntelSub>
                </Box>
              </DarkIntelHead>

              <DarkIntelTabsRow>
                <DarkIntelTabs>
                  <DarkIntelTab
                    active={intelTab === 'tweets'}
                    onClick={() => setIntelTab('tweets')}
                  >
                    Tweets <span className="ct">{buildTweets.length}</span>
                  </DarkIntelTab>
                  <DarkIntelTab
                    active={intelTab === 'trades'}
                    onClick={() => setIntelTab('trades')}
                  >
                    Trades <span className="ct">{(partnerTrades || []).length}</span>
                  </DarkIntelTab>
                </DarkIntelTabs>
                {intelTab === 'tweets' ? (
                  <DarkAttachToggle onClick={() => setIncludeTweets((v) => !v)}>
                    <DarkAttachSwitch on={!!includeTweets} />
                    <DarkAttachLabel>
                      Attach tweets to package
                      <span className="sub">
                        Recipient sees them when they open your trade · trades stay private
                      </span>
                    </DarkAttachLabel>
                  </DarkAttachToggle>
                ) : (
                  <DarkTradesInfo>For your reference — not attached to the package</DarkTradesInfo>
                )}
              </DarkIntelTabsRow>

              {intelTab === 'trades' && (
                <DarkSubFilters>
                  <DarkSubFilter
                    active={tradesFilter === 'this-trade'}
                    onClick={() => handleTradesFilterChange('this-trade')}
                  >
                    In this deal{' '}
                    <span className="ct">{intelTradeCounts['this-trade']}</span>
                  </DarkSubFilter>
                  <DarkSubFilter
                    active={tradesFilter === 'my-players'}
                    onClick={() => handleTradesFilterChange('my-players')}
                  >
                    With your players{' '}
                    <span className="ct">{intelTradeCounts['my-players']}</span>
                  </DarkSubFilter>
                  <DarkSubFilter
                    active={tradesFilter === 'all-theirs'}
                    onClick={() => handleTradesFilterChange('all-theirs')}
                  >
                    Across their leagues{' '}
                    <span className="ct">{intelTradeCounts['all-theirs']}</span>
                  </DarkSubFilter>
                  <DarkSubFilter
                    active={tradesFilter === 'any-user'}
                    onClick={() => handleTradesFilterChange('any-user')}
                  >
                    By other managers{' '}
                    <span className="ct">{intelTradeCounts['any-user']}</span>
                  </DarkSubFilter>
                </DarkSubFilters>
              )}

              <DarkIntelCards>
                {intelTab === 'tweets' ? (
                  buildTweets.length === 0 ? (
                    <DarkIntelEmpty>
                      {currentTradeIdSet.size === 0
                        ? 'Pick players for this trade to surface tweets.'
                        : 'No tweets matched these players yet.'}
                    </DarkIntelEmpty>
                  ) : (
                    buildTweets.map((t) => {
                      const tweetUrl = t.xTweetId
                        ? `https://x.com/${t.authorHandle || 'i'}/status/${t.xTweetId}`
                        : null;
                      return (
                        <DarkTweetCard
                          key={t.id}
                          included={!!includeTweets}
                          href={tweetUrl || '#'}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <DarkTweetRow>
                            <DarkTweetAv>
                              {t.authorAvatarUrl ? (
                                <img src={t.authorAvatarUrl} alt="" />
                              ) : (
                                (t.authorName || t.authorHandle || '?').toString().toUpperCase()[0]
                              )}
                            </DarkTweetAv>
                            <DarkTweetWho>
                              <DarkTweetName>{t.authorName || t.authorHandle}</DarkTweetName>
                              <DarkTweetHandle>@{t.authorHandle}</DarkTweetHandle>
                            </DarkTweetWho>
                            <DarkTweetXMark aria-label="View on X">𝕏</DarkTweetXMark>
                          </DarkTweetRow>
                          <DarkTweetText>{t.text}</DarkTweetText>
                          <DarkTweetFoot>
                            <span>
                              {t.tweetCreatedAt
                                ? formatRelativeTime(t.tweetCreatedAt)
                                : ''}
                            </span>
                            <span style={{ color: '#6E6E73' }}>
                              View on X ↗
                            </span>
                          </DarkTweetFoot>
                        </DarkTweetCard>
                      );
                    })
                  )
                ) : tradesFilter === 'any-user' ? (
                  currentTradeIdSet.size === 0 && currentTradePickKeySet.size === 0 ? (
                    <DarkIntelEmpty>
                      Pick players or picks for this trade to surface comps from any user.
                    </DarkIntelEmpty>
                  ) : compsLoading && comps.length === 0 ? (
                    <DarkIntelEmpty>Searching trade comps…</DarkIntelEmpty>
                  ) : comps.length === 0 ? (
                    <DarkIntelEmpty>
                      No matching trades across all synced users yet.
                    </DarkIntelEmpty>
                  ) : (
                    comps.map((t) => {
                      // Show every side as its own "received" block — easier
                      // to read than gave/got because each user's column lists
                      // exactly what they took home from the trade.
                      const sides = t.sides || [];
                      // Subject ordering: side that received a matched player/pick first.
                      const sideMatchesGot = (s) => {
                        const got = Object.keys(s.adds || {});
                        if (got.some((id) => currentTradeIdSet.has(String(id)))) return true;
                        return (s.picksGot || []).some((p) =>
                          currentTradePickKeySet.has(`${p.season}:${p.round}`)
                        );
                      };
                      // Subject (matched side) goes RIGHT; non-matching sides go LEFT.
                      const subjectIdx = sides.findIndex(sideMatchesGot);
                      const subjectSide = subjectIdx >= 0 ? sides[subjectIdx] : sides[sides.length - 1];
                      const otherSides = sides.filter((s) => s !== subjectSide);
                      // For 2-side trades there's exactly one other side. Multi-side trades
                      // (rare) fold all "other" players into the left column.
                      const leftAdds = otherSides.flatMap((s) => Object.keys(s.adds || {}));
                      const leftPicks = otherSides.flatMap((s) => s.picksGot || []);
                      const leftName =
                        otherSides.length === 1
                          ? otherSides[0].sleeperUsername ||
                            otherSides[0].displayName ||
                            `Roster ${otherSides[0].rosterId}`
                          : otherSides
                              .map((s) => s.sleeperUsername || s.displayName)
                              .filter(Boolean)
                              .join(' / ') || 'counterparty';
                      const subjectAdds = Object.keys(subjectSide?.adds || {});
                      const subjectPicks = subjectSide?.picksGot || [];
                      const subjectName =
                        subjectSide?.sleeperUsername ||
                        subjectSide?.displayName ||
                        (subjectSide ? `Roster ${subjectSide.rosterId}` : '?');
                      const subjectScoutHandle =
                        subjectSide?.sleeperUsername || null;
                      const compSettingsLine = formatLeagueSettings(t.league);
                      return (
                        <DarkDealCard key={t.id}>
                          <DarkDealHead>
                            <Box>
                              <DarkDealWhen>
                                {formatRelativeTime(t.tradedAt)}
                                {t.league?.name ? (
                                  <span className="league">· {t.league.name}</span>
                                ) : null}
                              </DarkDealWhen>
                              {compSettingsLine ? (
                                <DarkDealSettings>{compSettingsLine}</DarkDealSettings>
                              ) : null}
                            </Box>
                            {subjectScoutHandle && t.league?.id ? (
                              <DarkDealRosterLink
                                href={`/manager-intel/${subjectScoutHandle}?view=rosters&leagueId=${t.league.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                See roster ↗
                              </DarkDealRosterLink>
                            ) : null}
                          </DarkDealHead>
                          <DarkDealBody>
                            {/* LEFT col: counterparty */}
                            <DarkDealParty>
                              <DarkDealPartyName>@{leftName}</DarkDealPartyName>
                              <DarkDealPartyLabel>received</DarkDealPartyLabel>
                              <DarkDealPills>
                                {leftAdds.map((id) =>
                                  renderDealPill(
                                    id,
                                    currentTradeIdSet.has(String(id)) ||
                                      myRosterIdSet.has(String(id))
                                  )
                                )}
                                {leftPicks.map((p, i) =>
                                  renderPickPill(p, `cl-${i}`)
                                )}
                                {leftAdds.length + leftPicks.length === 0 && (
                                  <DarkDealPill>—</DarkDealPill>
                                )}
                              </DarkDealPills>
                            </DarkDealParty>
                            {/* RIGHT col: subject (matched side) */}
                            <DarkDealParty>
                              <DarkDealPartyName>@{subjectName}</DarkDealPartyName>
                              <DarkDealPartyLabel>received</DarkDealPartyLabel>
                              <DarkDealPills>
                                {subjectAdds.map((id) =>
                                  renderDealPill(
                                    id,
                                    currentTradeIdSet.has(String(id)) ||
                                      myRosterIdSet.has(String(id))
                                  )
                                )}
                                {subjectPicks.map((p, i) =>
                                  renderPickPill(p, `cr-${i}`)
                                )}
                                {subjectAdds.length + subjectPicks.length === 0 && (
                                  <DarkDealPill>—</DarkDealPill>
                                )}
                              </DarkDealPills>
                            </DarkDealParty>
                          </DarkDealBody>
                        </DarkDealCard>
                      );
                    })
                  )
                ) : filteredPartnerTrades.length === 0 ? (
                  <DarkIntelEmpty>
                    {tradesFilter === 'this-trade' && currentTradeIdSet.size === 0
                      ? 'Pick players for this trade to surface comp trades.'
                      : 'No matching trades.'}
                  </DarkIntelEmpty>
                ) : (
                  filteredPartnerTrades.map((t) => {
                    // Partner perspective: adds = what partner received, drops = what counterparty received.
                    const partnerAdds = Object.keys(t.partnerAdds || {});
                    const partnerDrops = Object.keys(t.partnerDrops || {});
                    const partnerPicksGot = (t.draftPicksTraded || []).filter(
                      (p) => p.toRosterId === t.partnerRosterId
                    );
                    const partnerPicksGave = (t.draftPicksTraded || []).filter(
                      (p) => p.fromRosterId === t.partnerRosterId
                    );
                    const counterpartyName =
                      t.counterparties?.[0]?.displayName || 'counterparty';
                    const partnerSettingsLine = formatLeagueSettings(t.league);
                    return (
                      <DarkDealCard key={t.id}>
                        <DarkDealHead>
                          <Box>
                            <DarkDealWhen>
                              {formatRelativeTime(t.tradedAt)}
                              {t.league?.name ? (
                                <span className="league">· {t.league.name}</span>
                              ) : null}
                            </DarkDealWhen>
                            {partnerSettingsLine ? (
                              <DarkDealSettings>{partnerSettingsLine}</DarkDealSettings>
                            ) : null}
                          </Box>
                          {partnerHandle && t.league?.id ? (
                            <DarkDealRosterLink
                              href={`/manager-intel/${partnerHandle}?view=rosters&leagueId=${t.league.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              See roster ↗
                            </DarkDealRosterLink>
                          ) : null}
                        </DarkDealHead>
                        <DarkDealBody>
                          {/* LEFT col: counterparty */}
                          <DarkDealParty>
                            <DarkDealPartyName>@{counterpartyName}</DarkDealPartyName>
                            <DarkDealPartyLabel>received</DarkDealPartyLabel>
                            <DarkDealPills>
                              {partnerDrops.map((id) =>
                                renderDealPill(
                                    id,
                                    currentTradeIdSet.has(String(id)) ||
                                      myRosterIdSet.has(String(id))
                                  )
                              )}
                              {partnerPicksGave.map((p, i) =>
                                renderPickPill(p, `pgave-${i}`)
                              )}
                              {partnerDrops.length + partnerPicksGave.length === 0 && (
                                <DarkDealPill>—</DarkDealPill>
                              )}
                            </DarkDealPills>
                          </DarkDealParty>
                          {/* RIGHT col: partner being scouted (the subject) */}
                          <DarkDealParty>
                            <DarkDealPartyName>
                              @{partnerHandle || partnerName}
                            </DarkDealPartyName>
                            <DarkDealPartyLabel>received</DarkDealPartyLabel>
                            <DarkDealPills>
                              {partnerAdds.map((id) =>
                                renderDealPill(
                                    id,
                                    currentTradeIdSet.has(String(id)) ||
                                      myRosterIdSet.has(String(id))
                                  )
                              )}
                              {partnerPicksGot.map((p, i) =>
                                renderPickPill(p, `pgot-${i}`)
                              )}
                              {partnerAdds.length + partnerPicksGot.length === 0 && (
                                <DarkDealPill>—</DarkDealPill>
                              )}
                            </DarkDealPills>
                          </DarkDealParty>
                        </DarkDealBody>
                      </DarkDealCard>
                    );
                  })
                )}
              </DarkIntelCards>
            </DarkIntel>
          </DarkCenterStage>

          {renderRosterPane('right')}

          <MobileRosterTabs>
            <MobileRosterTab
              active={mobileRosterTab === 'left'}
              onClick={() => setMobileRosterTab('left')}
            >
              <UserAvatarImg
                avatar={userOwner?.avatar}
                fallback={yourName}
                size={22}
                tone="you"
              />
              Your roster <span className="ct">{yourRoster.length}</span>
            </MobileRosterTab>
            <MobileRosterTab
              active={mobileRosterTab === 'right'}
              onClick={() => setMobileRosterTab('right')}
            >
              <UserAvatarImg
                avatar={opponentOwner?.avatar}
                fallback={opponentOwner?.username || partnerName}
                size={22}
              />
              {opponentOwner?.display_name || opponentOwner?.username || 'Partner'}{' '}
              <span className="ct">{theirRoster.length}</span>
            </MobileRosterTab>
          </MobileRosterTabs>

          <DarkBottom>
            <DarkBottomLeft>
              <DarkReviewLink
                onClick={
                  canAdvanceFromBuild
                    ? () => {
                        setDeckPhase('review');
                        setDeckOpen(true);
                      }
                    : undefined
                }
                disabled={!canAdvanceFromBuild}
                style={{
                  cursor: canAdvanceFromBuild ? 'pointer' : 'not-allowed',
                  opacity: canAdvanceFromBuild ? 1 : 0.5,
                }}
              >
                <DarkDeckMini />
                <b>
                  {totalTradesInPackage} trade{totalTradesInPackage === 1 ? '' : 's'}
                </b>{' '}
                in package
                <span style={{ opacity: 0.5 }}>· review →</span>
              </DarkReviewLink>
              <span style={{ color: '#86868B' }}>
                {includeTweets ? buildTweets.length : 0} tweet
                {buildTweets.length === 1 ? '' : 's'} attached
              </span>
            </DarkBottomLeft>
            <DarkActions>
              <DarkBtn
                onClick={addToPackage}
                disabled={yourSide.length === 0 || theirSide.length === 0}
              >
                Add trade to package
              </DarkBtn>
              <DarkBtn
                variant="primary"
                onClick={goNext}
                disabled={!canAdvanceFromBuild}
              >
                Send package →
              </DarkBtn>
            </DarkActions>
          </DarkBottom>
        </DarkStage>

        {/* Deck-review modal — slider of locked trades + the in-progress draft */}
        {deckOpen && (() => {
          const inProgress =
            yourSide.length > 0 && theirSide.length > 0
              ? [{
                  id: 'inprogress',
                  yourSide,
                  theirSide,
                  inProgress: true,
                }]
              : [];
          const allTrades = [...packageTrades, ...inProgress];
          return (
            <DarkDeckOverlay
              onClick={(e) => {
                if (e.target === e.currentTarget) setDeckOpen(false);
              }}
            >
              <DarkDeckPanel>
                <DarkDeckHead>
                  <DarkDeckTitle>
                    <span className="lbl">
                      {deckPhase === 'email'
                        ? 'One last step'
                        : deckPhase === 'verify'
                        ? 'Check your email'
                        : deckPhase === 'share'
                        ? 'Package ready'
                        : 'Your package'}
                    </span>
                    <span className="h">
                      {deckPhase === 'email'
                        ? 'Where should we send this?'
                        : deckPhase === 'verify'
                        ? 'Verify your email'
                        : deckPhase === 'share'
                        ? `Send to @${partnerHandle || partnerName} on Sleeper`
                        : `${allTrades.length} trade${allTrades.length === 1 ? '' : 's'} for @${partnerHandle || partnerName}`}
                    </span>
                  </DarkDeckTitle>
                  <DarkDeckClose onClick={() => setDeckOpen(false)} aria-label="Close">
                    ✕
                  </DarkDeckClose>
                </DarkDeckHead>
                {deckPhase === 'review' && <>
                  <DarkDeckSlider>
                  {allTrades.map((t, idx) => {
                    const tradeBal = computeBalance(t.yourSide, t.theirSide);
                    const building = !!t.inProgress;
                    const renderPill = (p, keyPrefix) => {
                      if (p.position === 'PICK') {
                        const round = p.round;
                        const suffix =
                          round === 1 ? 'st' : round === 2 ? 'nd' : round === 3 ? 'rd' : 'th';
                        return (
                          <DarkDeckPill
                            key={`${keyPrefix}-${p.id}`}
                            building={building}
                          >
                            <span className="face">PCK</span>
                            {p.season} {round}
                            {suffix}
                          </DarkDeckPill>
                        );
                      }
                      const url = sleeperPlayerThumb(p.id);
                      return (
                        <DarkDeckPill
                          key={`${keyPrefix}-${p.id}`}
                          building={building}
                        >
                          <span className="face">
                            {url ? (
                              <img
                                src={url}
                                alt=""
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                  e.currentTarget.parentNode.textContent = (
                                    p.position || '?'
                                  ).slice(0, 2);
                                }}
                              />
                            ) : (
                              (p.position || '?').slice(0, 2)
                            )}
                          </span>
                          {p.name}
                        </DarkDeckPill>
                      );
                    };
                    return (
                      <DarkDeckCard key={t.id} building={building}>
                        <DarkDeckCardHead building={building}>
                          <Box className="num">
                            Trade {idx + 1}
                            {building && <span className="tag">· building</span>}
                          </Box>
                          <DarkDeckBalance tone={tradeBal.tone} building={building}>
                            {tradeBal.label}
                            {tradeBal.sub ? ` · ${tradeBal.sub.toLowerCase()}` : ''}
                          </DarkDeckBalance>
                        </DarkDeckCardHead>
                        <DarkDeckSide>
                          <DarkDeckSideLbl building={building}>You give</DarkDeckSideLbl>
                          <DarkDeckPills>
                            {t.yourSide.length === 0 ? (
                              <DarkDeckPill building={building}>—</DarkDeckPill>
                            ) : (
                              t.yourSide.map((p) => renderPill(p, `give-${t.id}`))
                            )}
                          </DarkDeckPills>
                        </DarkDeckSide>
                        <DarkDeckArrow building={building}>↓</DarkDeckArrow>
                        <DarkDeckSide>
                          <DarkDeckSideLbl building={building}>You get</DarkDeckSideLbl>
                          <DarkDeckPills>
                            {t.theirSide.length === 0 ? (
                              <DarkDeckPill building={building}>—</DarkDeckPill>
                            ) : (
                              t.theirSide.map((p) => renderPill(p, `get-${t.id}`))
                            )}
                          </DarkDeckPills>
                        </DarkDeckSide>
                        <DarkDeckCardFoot building={building}>
                          <DarkDeckCardFootInfo building={building}>
                            {building ? 'Editing now' : `Trade ${idx + 1}`}
                          </DarkDeckCardFootInfo>
                          <DarkDeckCardActions>
                            {building ? (
                              <DarkDeckCardBtn
                                building
                                onClick={() => setDeckOpen(false)}
                              >
                                Resume building
                              </DarkDeckCardBtn>
                            ) : (
                              <>
                                <DarkDeckCardBtn
                                  onClick={() => {
                                    loadTradeForEditing(idx);
                                    setDeckOpen(false);
                                  }}
                                >
                                  Edit
                                </DarkDeckCardBtn>
                                <DarkDeckCardBtn
                                  danger
                                  onClick={() => {
                                    const remaining =
                                      packageTrades.length - 1 + inProgress.length;
                                    removeFromPackage(t.id);
                                    if (remaining <= 0) setDeckOpen(false);
                                  }}
                                >
                                  Remove
                                </DarkDeckCardBtn>
                              </>
                            )}
                          </DarkDeckCardActions>
                        </DarkDeckCardFoot>
                      </DarkDeckCard>
                    );
                  })}
                  <DarkDeckGhostCard
                    onClick={() => {
                      // Lock in the in-progress trade (if any) and clear the board
                      // so the user lands on a fresh canvas ready for the next trade.
                      if (yourSide.length > 0 && theirSide.length > 0) {
                        addToPackage();
                      }
                      setDeckOpen(false);
                    }}
                  >
                    <DarkDeckGhostIcon>+</DarkDeckGhostIcon>
                    <DarkDeckGhostHead>Add another trade.</DarkDeckGhostHead>
                    {allTrades.length <= 1 && (
                      <DarkDeckGhostBody>
                        Packages with 2–4 trades land deals more often. Each option
                        gives them another way to say yes — and reveals what they
                        actually value.
                      </DarkDeckGhostBody>
                    )}
                  </DarkDeckGhostCard>
                </DarkDeckSlider>
                <Box
                  sx={{
                    padding: '0 32px 8px',
                    maxWidth: '560px',
                    margin: '0 auto',
                    width: '100%',
                    boxSizing: 'border-box',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'baseline',
                      justifyContent: 'space-between',
                      marginBottom: '8px',
                    }}
                  >
                    <Box
                      sx={{
                        fontSize: '11px',
                        fontWeight: 600,
                        letterSpacing: '1.5px',
                        textTransform: 'uppercase',
                        color: '#86868B',
                      }}
                    >
                      Note for {partnerName}
                      <Box
                        component="span"
                        sx={{
                          marginLeft: '8px',
                          fontSize: '10px',
                          fontWeight: 500,
                          letterSpacing: '1px',
                          color: '#86868B',
                        }}
                      >
                        Optional
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        fontSize: '11px',
                        color:
                          packageMessage.length > PACKAGE_MESSAGE_MAX
                            ? '#FF3B30'
                            : '#86868B',
                        letterSpacing: '-0.01em',
                      }}
                    >
                      {packageMessage.length}/{PACKAGE_MESSAGE_MAX}
                    </Box>
                  </Box>
                  <Box
                    component="textarea"
                    rows={3}
                    placeholder={`A quick note — why this works for both of you.`}
                    value={packageMessage}
                    onChange={(e) =>
                      setPackageMessage(e.target.value.slice(0, PACKAGE_MESSAGE_MAX))
                    }
                    sx={{
                      width: '100%',
                      background: '#F5F5F7',
                      border: '1px solid #EBEBED',
                      borderRadius: '12px',
                      padding: '14px 16px',
                      fontSize: '15px',
                      color: '#1D1D1F',
                      letterSpacing: '-0.01em',
                      lineHeight: 1.5,
                      fontFamily: 'inherit',
                      outline: 'none',
                      boxSizing: 'border-box',
                      resize: 'vertical',
                      minHeight: '88px',
                      '&::placeholder': { color: '#86868B' },
                      '&:focus': {
                        background: '#FFFFFF',
                        borderColor: '#D2D2D7',
                        boxShadow: '0 0 0 4px rgba(0,0,0,0.08)',
                      },
                    }}
                  />
                </Box>
                </>}

                {deckPhase === 'email' && (
                  <Box sx={{ padding: '32px 32px 8px', maxWidth: '520px', margin: '0 auto', width: '100%' }}>
                    {sendError && (
                      <Box
                        sx={{
                          background: 'rgba(255,59,48,0.08)',
                          border: '1px solid rgba(255,59,48,0.20)',
                          borderRadius: '12px',
                          padding: '10px 14px',
                          marginBottom: '16px',
                          fontSize: '13px',
                          color: '#FF3B30',
                          letterSpacing: '-0.01em',
                        }}
                      >
                        {sendError}
                      </Box>
                    )}
                    <Box
                      sx={{
                        fontSize: '11px',
                        fontWeight: 600,
                        letterSpacing: '1.5px',
                        textTransform: 'uppercase',
                        color: '#86868B',
                        marginBottom: '8px',
                      }}
                    >
                      Your email
                    </Box>
                    <Box
                      component="input"
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      placeholder="you@example.com"
                      value={pendingEmail}
                      autoFocus
                      onChange={(e) => setPendingEmail(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && canAdvanceFromSignin) handleSendPackage();
                      }}
                      sx={{
                        width: '100%',
                        background: '#F5F5F7',
                        border: '1px solid #EBEBED',
                        borderRadius: '12px',
                        padding: '16px 18px',
                        fontSize: '17px',
                        color: '#1D1D1F',
                        letterSpacing: '-0.01em',
                        fontFamily: 'inherit',
                        outline: 'none',
                        boxSizing: 'border-box',
                        '&::placeholder': { color: '#86868B' },
                        '&:focus': {
                          background: '#FFFFFF',
                          borderColor: '#D2D2D7',
                          boxShadow: '0 0 0 4px rgba(0,0,0,0.08)',
                        },
                      }}
                    />
                    <Box
                      sx={{
                        fontSize: '14px',
                        color: '#6E6E73',
                        letterSpacing: '-0.01em',
                        marginTop: '14px',
                        lineHeight: 1.55,
                      }}
                    >
                      We'll email a magic link. Open it to verify your email,
                      unlock the share link for {partnerName}, and get notified
                      when they reply.
                    </Box>
                  </Box>
                )}

                {deckPhase === 'verify' && (
                  <Box sx={{ padding: '32px 32px 8px', maxWidth: '520px', margin: '0 auto', width: '100%' }}>
                    <Box
                      sx={{
                        fontSize: '15px',
                        color: '#6E6E73',
                        letterSpacing: '-0.01em',
                        lineHeight: 1.55,
                        marginBottom: '20px',
                      }}
                    >
                      We sent a magic link to{' '}
                      <Box component="b" sx={{ color: '#1D1D1F', fontWeight: 600 }}>
                        {magicLinkSentTo}
                      </Box>
                      . Open it to verify your email and unlock your share link
                      for {partnerName}.
                    </Box>
                    <Box
                      sx={{
                        padding: '16px 18px',
                        background: '#F5F5F7',
                        border: '1px solid #EBEBED',
                        borderRadius: '12px',
                        fontSize: '13px',
                        color: '#6E6E73',
                        lineHeight: 1.55,
                        marginBottom: '12px',
                      }}
                    >
                      The link expires in 15 minutes. If you don't see it, check
                      your spam folder.
                    </Box>
                    <Box
                      component="button"
                      onClick={() => {
                        setMagicLinkSentTo(null);
                        setDeckPhase('email');
                      }}
                      sx={{
                        background: 'none',
                        border: 'none',
                        color: '#86868B',
                        fontSize: '13px',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        padding: '8px 0',
                        textDecoration: 'underline',
                        textUnderlineOffset: '3px',
                      }}
                    >
                      Use a different email
                    </Box>
                  </Box>
                )}

                {deckPhase === 'share' && (
                  <Box sx={{ padding: '32px 32px 8px', maxWidth: '560px', margin: '0 auto', width: '100%' }}>
                    <Box
                      sx={{
                        fontSize: '15px',
                        color: '#6E6E73',
                        letterSpacing: '-0.01em',
                        lineHeight: 1.55,
                        marginBottom: '20px',
                      }}
                    >
                      Copy this link and paste it into a Sleeper DM with{' '}
                      <Box component="b" sx={{ color: '#1D1D1F', fontWeight: 600 }}>
                        @{partnerHandle || partnerName}
                      </Box>
                      .
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        background: '#F5F5F7',
                        border: '1px solid #EBEBED',
                        borderRadius: '12px',
                        padding: '6px 6px 6px 14px',
                      }}
                    >
                      <Box
                        sx={{
                          flex: 1,
                          fontSize: '13px',
                          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                          color: '#1D1D1F',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          letterSpacing: 0,
                        }}
                        title={createdPackageLink}
                      >
                        {createdPackageLink}
                      </Box>
                      <Box
                        component="button"
                        onClick={copyLinkToClipboard}
                        sx={{
                          height: '36px',
                          padding: '0 16px',
                          borderRadius: '980px',
                          border: 0,
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                          fontSize: '13px',
                          fontWeight: 500,
                          letterSpacing: '-0.01em',
                          background: linkCopied ? '#34C759' : '#1D1D1F',
                          color: '#FFFFFF',
                          transition: 'background 0.2s ease',
                          '&:hover': { background: linkCopied ? '#34C759' : '#1D1D1F' },
                        }}
                      >
                        {linkCopied ? 'Copied' : 'Copy link'}
                      </Box>
                    </Box>

                    {magicLinkSentTo && (
                      <Box
                        sx={{
                          marginTop: '20px',
                          padding: '14px 16px',
                          background: '#F5F5F7',
                          border: '1px solid #EBEBED',
                          borderRadius: '12px',
                          fontSize: '13px',
                          color: '#6E6E73',
                          letterSpacing: '-0.01em',
                          lineHeight: 1.5,
                        }}
                      >
                        We also emailed{' '}
                        <Box component="b" sx={{ color: '#1D1D1F', fontWeight: 600 }}>
                          {magicLinkSentTo}
                        </Box>{' '}
                        a magic link — open it to claim this package and get notified
                        when {partnerName} responds.
                      </Box>
                    )}

                    <Box
                      sx={{
                        marginTop: '20px',
                        fontSize: '13px',
                        color: '#86868B',
                        letterSpacing: '-0.01em',
                        textAlign: 'center',
                      }}
                    >
                      Suggested message:{' '}
                      <Box component="span" sx={{ color: '#1D1D1F' }}>
                        "Hey, built you a few trade options. Take a look:"
                      </Box>
                    </Box>
                  </Box>
                )}

                <DarkDeckFoot>
                  <DarkDeckFootHint>
                    {deckPhase === 'review' &&
                      (allTrades.length === 0
                        ? 'No trades yet — pick players to build one.'
                        : 'Scroll to review · tap edit to tweak a trade')}
                    {deckPhase === 'email' && "We'll email you the magic link"}
                    {deckPhase === 'verify' && 'Verify to unlock your share link'}
                    {deckPhase === 'share' && (linkCopied ? 'Link copied' : 'Tap copy, then open Sleeper')}
                  </DarkDeckFootHint>
                  <DarkActions>
                    {deckPhase === 'review' && (
                      <>
                        <DarkBtn onClick={() => setDeckOpen(false)}>Keep building</DarkBtn>
                        <DarkBtn
                          variant="primary"
                          onClick={advanceFromReview}
                          disabled={!canAdvanceFromBuild || saving}
                        >
                          {saving
                            ? 'Sending…'
                            : user?.email
                            ? `Send package · ${allTrades.length} trade${allTrades.length === 1 ? '' : 's'} →`
                            : 'Continue →'}
                        </DarkBtn>
                      </>
                    )}
                    {deckPhase === 'email' && (
                      <>
                        <DarkBtn onClick={() => setDeckPhase('review')}>← Back</DarkBtn>
                        <DarkBtn
                          variant="primary"
                          onClick={handleSendPackage}
                          disabled={!canAdvanceFromSignin || saving || !canSend}
                        >
                          {saving
                            ? 'Sending…'
                            : `Send package · ${allTrades.length} trade${allTrades.length === 1 ? '' : 's'} →`}
                        </DarkBtn>
                      </>
                    )}
                    {deckPhase === 'verify' && (
                      <DarkBtn variant="primary" onClick={() => setDeckOpen(false)}>Done</DarkBtn>
                    )}
                    {deckPhase === 'share' && (
                      <>
                        <DarkBtn
                          onClick={() => {
                            if (createdPackageLink) {
                              window.open(createdPackageLink, '_blank', 'noopener,noreferrer');
                            }
                          }}
                        >
                          View package
                        </DarkBtn>
                        <DarkBtn variant="primary" onClick={() => setDeckOpen(false)}>
                          Done
                        </DarkBtn>
                      </>
                    )}
                  </DarkActions>
                </DarkDeckFoot>
              </DarkDeckPanel>
            </DarkDeckOverlay>
          );
        })()}

        {/* Manager Intel sync-complete toast */}
        {intelToastName && (
          <Box
            sx={{
              position: 'fixed',
              bottom: '80px',
              right: '24px',
              zIndex: 1000,
              background: '#1D1D1F',
              color: '#FFFFFF',
              padding: '14px 22px',
              borderRadius: '14px',
              fontSize: '14px',
              fontWeight: 500,
              letterSpacing: '-0.01em',
              boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}
          >
            Intel ready for {intelToastName}.
          </Box>
        )}
      </DarkPage>
    );
  }

  return (
    <PageContainer>
      <TradePackagesHeader dark />

      <Container>
        {/* Stepper */}
        <StepperWrap>
          {STEPS.map((s, i) => {
            const state = i < stepIndex ? 'done' : i === stepIndex ? 'active' : 'pending';
            const isClickable = i <= stepIndex;
            return (
              <React.Fragment key={s.id}>
                <Box
                  onClick={() => isClickable && setStep(s.id)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    cursor: isClickable ? 'pointer' : 'default',
                  }}
                >
                  <StepDot state={state}>{state === 'done' ? '✓' : i + 1}</StepDot>
                  <StepLabel state={state}>{s.label}</StepLabel>
                </Box>
                {i < STEPS.length - 1 && <StepLine />}
              </React.Fragment>
            );
          })}
        </StepperWrap>


        {/* SIGN IN STEP — only reached when the user has no email on file. */}
        {step === 'signin' && (
          <StepView>
            <ContextBar>
              <ContextAvatar
                sx={{
                  overflow: 'hidden',
                  '& img': { width: '100%', height: '100%', objectFit: 'cover' },
                }}
              >
                {opponentOwner?.avatar ? (
                  <img
                    src={`https://sleepercdn.com/avatars/thumbs/${opponentOwner.avatar}`}
                    alt=""
                  />
                ) : (
                  (partnerName[0] || '?').toUpperCase()
                )}
              </ContextAvatar>
              <Box sx={{ flex: 1 }}>
                <ContextName>
                  {magicLinkSentTo ? 'Check your email.' : 'Sign in to send'}
                </ContextName>
                <ContextSub>
                  {magicLinkSentTo
                    ? `We sent a link to ${magicLinkSentTo}. Open it to share with ${partnerName}.`
                    : `We'll email a link so you can confirm and ${partnerName} can respond to you.`}
                </ContextSub>
              </Box>
            </ContextBar>

            {sendError && !magicLinkSentTo && (
              <Box
                sx={{
                  background: 'rgba(255,59,48,0.08)',
                  border: '1px solid rgba(255,59,48,0.20)',
                  borderRadius: '14px',
                  padding: '12px 16px',
                  marginBottom: '20px',
                  fontSize: '13px',
                  color: '#FF3B30',
                  letterSpacing: '-0.01em',
                }}
              >
                {sendError}
              </Box>
            )}

            {magicLinkSentTo ? (
              <Box
                sx={{
                  background: '#F5F5F7',
                  border: '1px solid #EBEBED',
                  borderRadius: '18px',
                  padding: '32px 24px',
                  marginTop: '20px',
                  textAlign: 'center',
                }}
              >
                <Box
                  sx={{
                    fontSize: '20px',
                    fontWeight: 600,
                    letterSpacing: '-0.01em',
                    color: '#1D1D1F',
                    marginBottom: '8px',
                  }}
                >
                  Magic link on the way.
                </Box>
                <Box
                  sx={{
                    fontSize: '14px',
                    color: '#6E6E73',
                    letterSpacing: '-0.01em',
                    lineHeight: 1.5,
                  }}
                >
                  Open the link in your inbox to send the package to {partnerName}.
                  We'll email you the moment they respond.
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  background: '#F5F5F7',
                  border: '1px solid #EBEBED',
                  borderRadius: '18px',
                  padding: '24px',
                  marginTop: '20px',
                }}
              >
                <Box
                  sx={{
                    fontSize: '11px',
                    fontWeight: 600,
                    letterSpacing: '1.5px',
                    textTransform: 'uppercase',
                    color: '#86868B',
                    marginBottom: '8px',
                  }}
                >
                  Your email
                </Box>
                <Box
                  component="input"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  value={pendingEmail}
                  autoFocus
                  onChange={(e) => setPendingEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && canAdvanceFromSignin) goNext();
                  }}
                  sx={{
                    width: '100%',
                    background: '#FFFFFF',
                    border: '1px solid #EBEBED',
                    borderRadius: '12px',
                    padding: '16px 18px',
                    fontSize: '17px',
                    color: '#1D1D1F',
                    letterSpacing: '-0.01em',
                    fontFamily: 'inherit',
                    outline: 'none',
                    boxSizing: 'border-box',
                    '&::placeholder': { color: '#86868B' },
                    '&:focus': {
                      background: '#FFFFFF',
                      borderColor: '#D2D2D7',
                      boxShadow: '0 0 0 4px rgba(0,0,0,0.08)',
                    },
                  }}
                />
                <Box
                  sx={{
                    fontSize: '13px',
                    color: '#6E6E73',
                    letterSpacing: '-0.01em',
                    marginTop: '12px',
                  }}
                >
                  When you hit send, we'll email a magic link. Open it to share the
                  package with {partnerName}.
                </Box>
              </Box>
            )}
          </StepView>
        )}
      </Container>

      {/* Sticky bottom nav */}
      <StickyNav>
        <StickyInner>
          <StickyHint>
            {step === 'build' &&
              (totalTradesInPackage > 0
                ? `${totalTradesInPackage} trade${totalTradesInPackage === 1 ? '' : 's'} in your package`
                : 'Pick players from both rosters to start')}
            {step === 'signin' &&
              (magicLinkSentTo
                ? `Magic link sent to ${magicLinkSentTo}`
                : "We'll email a link so you can confirm it's you")}
          </StickyHint>
          <NavBtns>
            {step === 'signin' && !magicLinkSentTo && (
              <Pill onClick={goBack}>← Back</Pill>
            )}
            {step === 'build' && (
              <Pill
                variant="primary"
                onClick={goNext}
                disabled={!canAdvanceFromBuild || saving}
              >
                {saving
                  ? 'Sending…'
                  : `Send package · ${totalTradesInPackage} trade${totalTradesInPackage === 1 ? '' : 's'} →`}
              </Pill>
            )}
            {step === 'signin' && !magicLinkSentTo && (
              <Pill
                variant="primary"
                onClick={goNext}
                disabled={!canAdvanceFromSignin || saving || !canSend}
              >
                {saving
                  ? 'Sending…'
                  : `Send package · ${totalTradesInPackage} trade${totalTradesInPackage === 1 ? '' : 's'} →`}
              </Pill>
            )}
          </NavBtns>
        </StickyInner>
      </StickyNav>

      {/* Manager Intel sync-complete toast */}
      {intelToastName && (
        <Box
          sx={{
            position: 'fixed',
            bottom: '100px',
            right: '24px',
            zIndex: 1000,
            background: '#000000',
            color: '#FFFFFF',
            padding: '14px 22px',
            borderRadius: '14px',
            fontSize: '14px',
            fontWeight: 500,
            letterSpacing: '-0.01em',
            boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            animation: `${fadeIn} 0.3s cubic-bezier(0.16, 1, 0.3, 1)`,
            '@media (max-width: 600px)': {
              left: '16px',
              right: '16px',
              bottom: '90px',
            },
          }}
        >
          <Box sx={{ fontSize: '16px' }}>✓</Box>
          {intelToastName}'s intel is ready
        </Box>
      )}
    </PageContainer>
  );
};

export default ScoutCanvas;
